import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  healthClasses,
  priorityClasses,
  statusClasses,
  ticketPriorities,
  ticketStatuses,
  ticketTabs,
} from "../app/data";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { AnimatedModal, Avatar, TicketDurationText, TicketLastUpdateText, TicketStatCard, UserAvatarBadge } from "../app/ui";
import { parseTicketNotes } from "../app/ticketNotes";
import {
  formatCount,
  formatScheduleRangeLabel,
  formatPreciseDateTimeLabel,
  formatTicketSlaLabel,
  getInitials,
  getAssignableAssigneeNames,
  getCustomerSeatCount,
  getTicketSlaCountdownInfo,
  getTicketSlaDeadlineTimestamp,
  getStatusIcon,
  normalizeDateTimeForStorage,
  parseTimestampValue,
  resolveWorkspaceMember,
  syncTicketCustomFieldValues,
} from "../app/utils";
import { ticketsViewCopy } from "../app/i18n";
import type { AppLanguage } from "../app/language";
import type { Customer, ProductPlan, Ticket, TicketCategory, TicketCategoryCustomField, TicketCounts, TicketCreateInput, TicketPriority, TicketScope, TicketStatus, WorkspaceMember } from "../app/types";

type TicketsViewCopy = typeof ticketsViewCopy.en;

type TicketQuickViewProps = {
  ticket: Ticket;
  customer: Customer;
  productPlans: ProductPlan[];
  timezone: string;
  copy: TicketsViewCopy;
  currentTime: number;
  currentUser: string;
  ticketCategories: TicketCategory[];
  teamMembers: WorkspaceMember[];
  canEditTickets: boolean;
  onAssignTicket: (id: string, assignee: string | null) => void;
  onUpdateCategory: (id: string, category: string) => void;
  onUpdateProduct: (id: string, product: string) => void;
  onUpdatePriority: (id: string, priority: TicketPriority) => void;
  onUpdateStatus: (id: string, status: TicketStatus) => void;
  onOpenTicket: (id: string) => void;
  onClose: () => void;
};

type TicketPillMenuProps<T extends string> = {
  value: T;
  options: readonly T[];
  ariaLabel: string;
  buttonClassName: string;
  menuClassName?: string;
  onChange: (value: T) => void;
  renderValue: (value: T) => ReactNode;
  renderOption?: (value: T) => ReactNode;
  getOptionClassName?: (value: T) => string;
};

function TicketPillMenu<T extends string>({
  value,
  options,
  ariaLabel,
  buttonClassName,
  menuClassName,
  onChange,
  renderValue,
  renderOption,
  getOptionClassName,
}: TicketPillMenuProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; origin: "top" | "bottom" } | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const viewportPadding = 12;
    const menuWidth = menuRef.current?.offsetWidth ?? 176;
    const menuHeight = menuRef.current?.offsetHeight ?? 220;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;
    const centeredLeft = rect.left + rect.width / 2 - menuWidth / 2;
    const left = Math.min(
      Math.max(viewportPadding, centeredLeft),
      window.innerWidth - menuWidth - viewportPadding,
    );

    setMenuPosition({
      top: shouldOpenUpward ? Math.max(viewportPadding, rect.top - menuHeight - 8) : rect.bottom + 8,
      left,
      origin: shouldOpenUpward ? "bottom" : "top",
    });
  }, []);

  // Measure the rendered menu and position it before paint so the very first
  // open uses the menu's real size instead of the fallback estimate.
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node) || menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div ref={containerRef} className="relative inline-flex" onClick={event => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={event => {
          event.stopPropagation();
          setIsOpen(current => !current);
        }}
        className={`${buttonClassName} transition-all hover:brightness-[0.98] focus:outline-none`}
        style={{ boxShadow: "0 0 0 0 transparent" }}
      >
        {renderValue(value)}
        <span aria-hidden="true" className="text-[9px] text-current/70">
          ▾
        </span>
      </button>

      {isOpen
        ? createPortal(
            <div
              ref={menuRef}
              className={`fixed z-[220] min-w-[11rem] rounded-[22px] border border-[#eaecf0] bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.14)] ${menuClassName ?? ""}`}
              style={{
                top: menuPosition?.top ?? 0,
                left: menuPosition?.left ?? 0,
                visibility: menuPosition ? "visible" : "hidden",
                transformOrigin: menuPosition?.origin === "bottom" ? "bottom center" : "top center",
              }}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
            >
              {options.map(option => {
                const active = option === value;

                return (
                  <button
                    key={option}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={event => {
                      event.stopPropagation();
                      onChange(option);
                      setIsOpen(false);
                    }}
                    className={`mt-1 flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-semibold transition-colors first:mt-0 hover:bg-[#f8fafc] ${
                      getOptionClassName?.(option) ?? "text-[#101828]"
                    } ${active ? "bg-[#f3f4f6]" : ""}`}
                  >
                    <span className="flex items-center gap-2">{renderOption ? renderOption(option) : renderValue(option)}</span>
                    {active ? <span className="brand-primary-text">✓</span> : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function AssigneeIdentity({
  assignee,
  teamMembers,
  emptyLabel = "Unassigned",
  emptyDetail = "Needs owner",
  assignedLabel = "Assigned",
  avatarClassName = "brand-primary-avatar flex h-9 w-9 items-center justify-center rounded-full text-[10px] font-bold",
}: {
  assignee: string | null;
  teamMembers: WorkspaceMember[];
  emptyLabel?: string;
  emptyDetail?: string;
  assignedLabel?: string;
  avatarClassName?: string;
}) {
  const member = resolveWorkspaceMember(teamMembers, assignee);

  return (
    <div className="flex items-center gap-3">
      <UserAvatarBadge
        avatarUrl={member?.avatarUrl ?? null}
        initials={member?.initials ?? getInitials(assignee ?? emptyLabel)}
        className={avatarClassName}
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-[#1a1c1d]">{assignee ?? emptyLabel}</div>
        <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">{assignee ? assignedLabel : emptyDetail}</div>
      </div>
    </div>
  );
}

function TicketQuickView({ ticket, customer, productPlans, timezone, copy, currentTime, currentUser, ticketCategories, teamMembers, canEditTickets, onAssignTicket, onUpdateCategory, onUpdateProduct, onUpdatePriority, onUpdateStatus, onOpenTicket, onClose }: TicketQuickViewProps) {
  const hasCustomer = Boolean(ticket.customerId.trim() && customer.id.trim());
  const ticketCategoryNames = ticketCategories.map(category => category.name);
  const ticketCreatedAt = ticket.createdAt;
  const assignableAssignees = useMemo(() => getAssignableAssigneeNames(teamMembers, ticketCategories, ticket.category), [teamMembers, ticketCategories, ticket.category]);
  const itemOptions = useMemo(
    () => getTicketItemOptions(ticket.product, customer, productPlans, copy.none),
    [copy.none, customer, productPlans, ticket.product],
  );
  const slaCountdown = getTicketSlaCountdownInfo(ticket.createdAt, ticket.sla, currentTime, ticket.slaPausedAt, ticket.slaPausedTotalMs);

  return (
    <AnimatedModal
      open={true}
      onClose={onClose}
      backdropAriaLabel={copy.quickView.close}
      overlayClassName="fixed inset-0 z-[60] flex items-center justify-center bg-[#101828]/28 p-6 backdrop-blur-[4px]"
      dialogClassName="app-modal-dialog ticket-quick-view-shell relative z-10 max-h-[calc(100vh-6rem)] w-full max-w-6xl overflow-y-auto rounded-[36px] border border-white/70 p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
    >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.quickView.eyebrow}</p>
            <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#1a1c1d]">#{ticket.id}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
          <section className="flex h-full flex-col rounded-[28px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.quickView.ticketDetails}</p>
                <TicketLastUpdateText value={ticket.lastUpdate} timezone={timezone} className="text-sm font-semibold text-[#1a1c1d]" />
              </div>

              <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#1a1c1d]">{ticket.subject}</h3>
                  <div className="mt-3">
                    {canEditTickets ? (
                      <TicketPillMenu
                        value={ticket.category}
                        options={ticketCategoryNames.length > 0 ? ticketCategoryNames : [ticket.category || "General"]}
                        ariaLabel={copy.quickView.changeCategory}
                        onChange={category => onUpdateCategory(ticket.id, category)}
                        buttonClassName="inline-flex items-center gap-2 rounded-full bg-[#f8fafc] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#475467]"
                        renderValue={category => <span>{category}</span>}
                        renderOption={category => <span>{category}</span>}
                      />
                    ) : <span className="inline-flex items-center rounded-full bg-[#f8fafc] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#475467]">{ticket.category}</span>}
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  {canEditTickets ? (
                    <>
                      <TicketPillMenu
                        value={ticket.priority}
                        options={ticketPriorities}
                        ariaLabel={copy.quickView.changePriority}
                        onChange={priority => onUpdatePriority(ticket.id, priority)}
                        buttonClassName={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[ticket.priority]}`}
                        renderValue={priority => <span>{copy.priorityLabels[priority]}</span>}
                        renderOption={priority => <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[priority]}`}>{copy.priorityLabels[priority]}</span>}
                      />
                      <TicketPillMenu
                        value={ticket.status}
                        options={ticketStatuses}
                        ariaLabel={copy.quickView.changeStatus}
                        onChange={status => onUpdateStatus(ticket.id, status)}
                        buttonClassName={`inline-flex items-center gap-2 rounded-full bg-[#f2f4f7] px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${statusClasses[ticket.status]}`}
                        renderValue={status => (
                          <>
                            <span className="text-xs">{getStatusIcon(status)}</span>
                            <span>{copy.statusLabels[status]}</span>
                          </>
                        )}
                        renderOption={status => (
                          <>
                            <span className="text-base leading-none">{getStatusIcon(status)}</span>
                            <span>{copy.statusLabels[status]}</span>
                          </>
                        )}
                        getOptionClassName={status => statusClasses[status]}
                      />
                    </>
                  ) : (
                    <>
                      <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[ticket.priority]}`}>{copy.priorityLabels[ticket.priority]}</span>
                      <span className={`inline-flex items-center gap-2 rounded-full bg-[#f2f4f7] px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${statusClasses[ticket.status]}`}>{copy.statusLabels[ticket.status]}</span>
                    </>
                  )}
                  {canEditTickets && itemOptions.length > 0 ? (
                    <TicketPillMenu
                      value={ticket.product}
                      options={itemOptions}
                      ariaLabel={copy.quickView.changeItem}
                      onChange={product => onUpdateProduct(ticket.id, product)}
                      buttonClassName="inline-flex items-center gap-2 rounded-full bg-[#f3f3f4] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#475467]"
                      renderValue={product => <span>{getTicketItemDisplayLabel(product, copy.quickView.emptyItem, copy.none)}</span>}
                      renderOption={product => <span>{product}</span>}
                    />
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-[#f3f3f4] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#475467]">
                      {getTicketItemDisplayLabel(ticket.product, copy.quickView.emptyItem, copy.none)}
                    </span>
                  )}
                  {ticket.sla ? (
                    <span className="inline-flex items-center rounded-full bg-[#ecfdf3] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#027a48]">
                      SLA · {formatTicketSlaLabel(ticket.sla)}
                    </span>
                  ) : null}
                  {slaCountdown ? (
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${slaCountdown.isPaused ? "bg-[#fff7ed] text-[#c2410c]" : slaCountdown.isOverdue ? "bg-[#fef3f2] text-[#b42318]" : "bg-[#eff8ff] text-[#175cd3]"}`}>
                      {slaCountdown.isPaused ? `${copy.quickView.paused} · ${slaCountdown.displayLabel} ${copy.quickView.left}` : `${slaCountdown.displayLabel} ${slaCountdown.isOverdue ? copy.quickView.over : copy.quickView.left}`}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-6 flex-1 rounded-2xl bg-[#f9f9fa] p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.quickView.description}</p>
              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600">{ticket.description}</p>
            </div>

            <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                {canEditTickets ? <AssigneePicker ticket={ticket} assigneeOptions={assignableAssignees} teamMembers={teamMembers} currentUserName={currentUser} copy={copy} onAssign={assignee => onAssignTicket(ticket.id, assignee)} /> : <div className="rounded-xl bg-transparent px-3 py-2 text-left"><AssigneeIdentity assignee={ticket.assignee} teamMembers={teamMembers} emptyLabel={copy.unassigned} emptyDetail={copy.needsOwner} assignedLabel={copy.assigned} /></div>}
              </div>

              <div className="rounded-2xl bg-[#f8fafc] px-4 py-3 text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.quickView.duration}</p>
                <p className="mt-1 text-sm font-semibold text-[#1a1c1d]">{copy.quickView.open} <TicketDurationText value={ticketCreatedAt} /></p>
                <p className="mt-1 text-[11px] text-slate-400">{copy.quickView.created(formatPreciseDateTimeLabel(ticketCreatedAt, copy.quickView.unknown, timezone))}</p>
              </div>

              <div className="rounded-2xl bg-[#f8fafc] px-4 py-3 text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.quickView.schedule}</p>
                <p className="mt-1 text-sm font-semibold text-[#1a1c1d]">{formatScheduleRangeLabel(ticket.scheduleStartAt, ticket.scheduleAt, copy.quickView.noScheduleSet, timezone)}</p>
              </div>

              <button
                type="button"
                onClick={() => onOpenTicket(ticket.id)}
                className="brand-primary-button rounded-xl px-5 py-3 text-sm font-bold transition-all"
              >
                {copy.quickView.open}
              </button>
            </div>
          </section>

          <section className="rounded-[28px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            {hasCustomer ? (
              <>
                <div className="flex items-center gap-4">
                  <div className="brand-primary-avatar flex h-14 w-14 items-center justify-center rounded-full text-base font-bold">{customer.initials}</div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.quickView.customerDetails}</p>
                    <h3 className="mt-1 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#1a1c1d]">{customer.name}</h3>
                    <p className="text-sm text-slate-500">{customer.company}</p>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${healthClasses[customer.health]}`}>{customer.health}</span>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">{copy.quickView.email}</p>
                    <p className="mt-1 font-semibold text-[#1a1c1d]">{customer.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">{copy.quickView.phone}</p>
                    <p className="mt-1 font-semibold text-[#1a1c1d]">{customer.phone}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">{copy.quickView.region}</p>
                    <p className="mt-1 font-semibold text-[#1a1c1d]">{customer.region}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">{copy.quickView.productSeats}</p>
                    <p className="mt-1 font-semibold text-[#1a1c1d]">{getCustomerSeatCount(customer)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-400">{copy.quickView.renewalDate}</p>
                    <p className="mt-1 font-semibold text-[#1a1c1d]">{customer.renewalDate}</p>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl bg-[#f9f9fa] p-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.quickView.products}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {customer.products.map(product => (
                      <span key={product.name} className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,0.05)]">{product.name} · {product.seats} seats</span>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.quickView.customerDetails}</p>
                <p className="mt-3 text-lg font-semibold text-[#1a1c1d]">{copy.quickView.none}</p>
                <p className="mt-2 text-sm text-slate-500">{copy.quickView.noCustomerProfile}</p>
              </div>
            )}
          </section>
        </div>
    </AnimatedModal>
  );
}

type TicketsViewProps = {
  language: AppLanguage;
  customers: Customer[];
  productPlans: ProductPlan[];
  ticketCategories: TicketCategory[];
  teamMembers: WorkspaceMember[];
  currentUser: string;
  timezone: string;
  canEditTickets?: boolean;
  isLoadingTickets?: boolean;
  activeTab: TicketScope;
  onTabChange: (tab: TicketScope) => void;
  filteredTickets: Ticket[];
  syncingTicketIds?: string[];
  selectedTicket: Ticket;
  selectedCustomer: Customer;
  isQuickViewOpen: boolean;
  counts: TicketCounts;
  onSelectTicket: (id: string) => void;
  onCreateTicket: (input: TicketCreateInput) => void;
  onAssignTicket: (id: string, assignee: string | null) => void;
  onUpdateCategory: (id: string, category: string) => void;
  onUpdateProduct: (id: string, product: string) => void;
  onUpdatePriority: (id: string, priority: TicketPriority) => void;
  onUpdateStatus: (id: string, status: TicketStatus) => void;
  onOpenTicket: (id: string) => void;
  onOpenQuickView: (id: string) => void;
  onCloseQuickView: () => void;
};

type AssigneePickerProps = {
  ticket: Ticket;
  assigneeOptions: string[];
  teamMembers: WorkspaceMember[];
  currentUserName: string;
  copy: TicketsViewCopy;
  onAssign: (assignee: string | null) => void;
};

type TicketSortColumn = "id" | "subject" | "category" | "customer" | "item" | "sla" | "assignee" | "priority" | "status" | "duration" | "schedule" | "lastUpdate";
type TicketSortDirection = "asc" | "desc";
type TicketAssignmentFilter = "any" | "mine" | "assigned" | "unassigned";
type TicketScheduleFilter = "any" | "scheduled" | "unscheduled" | "overdue" | "upcoming";
type TicketUpdatedFilter = "any" | "24h" | "7d" | "30d";
const ticketColumnStorageKey = "kuii.ticketTable.visibleColumns";
const ticketColumnOrderStorageKey = "kuii.ticketTable.columnOrder";
const ticketPageSizeStorageKey = "kuii.ticketTable.pageSize";
const ticketTablePageSizeOptions = [10, 25, 50, 100] as const;
const requiredTicketColumns: TicketSortColumn[] = ["id", "subject"];

type TicketViewFilters = {
  priorities: TicketPriority[];
  statuses: TicketStatus[];
  categories: string[];
  customerIds: string[];
  products: string[];
  assignees: string[];
  assignment: TicketAssignmentFilter;
  schedule: TicketScheduleFilter;
  updatedWithin: TicketUpdatedFilter;
};
type FilterMultiSelectOption = {
  value: string;
  label: string;
  detail?: string;
  keywords?: string[];
};
type TicketCreateDraft = {
  subject: string;
  description: string;
  category: string;
  product: string;
  customerId: string;
  priority: TicketPriority;
  status: TicketStatus;
  scheduleStartAt: string;
  scheduleAt: string;
  assignee: string;
  customFieldValues: Record<string, string>;
};

function getTicketCategoryByName(ticketCategories: TicketCategory[], categoryName: string) {
  const normalizedCategoryName = categoryName.trim().toLowerCase();
  return ticketCategories.find(category => category.name.trim().toLowerCase() === normalizedCategoryName) ?? null;
}

function getCustomFieldValidationError(customFields: TicketCategoryCustomField[], customFieldValues: Record<string, string>, copy: TicketsViewCopy) {
  for (const field of customFields) {
    const value = customFieldValues[field.id] ?? "";

    if (field.required && !value.trim()) {
      return copy.createDialog.customFieldRequired(field.label);
    }
  }

  return null;
}

const ticketPriorityOrder: Record<TicketPriority, number> = {
  Low: 0,
  Medium: 1,
  High: 2,
};

const ticketStatusOrder: Record<TicketStatus, number> = {
  Open: 0,
  "In Progress": 1,
  "Waiting customer": 2,
  Pending: 3,
  Resolved: 4,
};

const ticketTableHeaders: Array<{ key: TicketSortColumn; label: string; className: string; canHide?: boolean }> = [
  { key: "id", label: "Ticket ID", className: "w-[8.5rem] px-8 whitespace-nowrap", canHide: false },
  { key: "subject", label: "Subject", className: "px-6", canHide: false },
  { key: "category", label: "Category", className: "px-6" },
  { key: "customer", label: "Customer", className: "px-6" },
  { key: "item", label: "Item", className: "px-6" },
  { key: "sla", label: "SLA", className: "w-[12rem] px-6 whitespace-nowrap" },
  { key: "priority", label: "Priority", className: "px-6" },
  { key: "status", label: "Status", className: "w-[10rem] px-6 whitespace-nowrap" },
  { key: "duration", label: "Duration", className: "w-[10rem] px-6 whitespace-nowrap" },
  { key: "schedule", label: "Schedule", className: "w-[12rem] px-6 whitespace-nowrap" },
  { key: "assignee", label: "Assignee", className: "px-6" },
  { key: "lastUpdate", label: "Last Update", className: "w-[14rem] px-6 whitespace-nowrap" },
];

function sanitizeVisibleTicketColumns(columns: TicketSortColumn[]) {
  const requestedColumns = new Set<TicketSortColumn>([...columns, ...requiredTicketColumns]);

  return ticketTableHeaders
    .map(header => header.key)
    .filter(column => requestedColumns.has(column));
}

function getInitialVisibleTicketColumns() {
  const allColumns = ticketTableHeaders.map(header => header.key);

  if (typeof window === "undefined") {
    return allColumns;
  }

  try {
    const storedValue = window.localStorage.getItem(ticketColumnStorageKey);

    if (!storedValue) {
      return allColumns;
    }

    const parsedValue = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) {
      return allColumns;
    }

    const validColumns = parsedValue.filter((value): value is TicketSortColumn =>
      typeof value === "string" && ticketTableHeaders.some(header => header.key === value),
    );

    return validColumns.length > 0 ? sanitizeVisibleTicketColumns(validColumns) : allColumns;
  } catch {
    return allColumns;
  }
}

function sanitizeTicketColumnOrder(columns: TicketSortColumn[]) {
  const seen = new Set<TicketSortColumn>();
  const ordered: TicketSortColumn[] = [];

  for (const column of columns) {
    if (ticketTableHeaders.some(header => header.key === column) && !seen.has(column)) {
      seen.add(column);
      ordered.push(column);
    }
  }

  for (const header of ticketTableHeaders) {
    if (!seen.has(header.key)) {
      ordered.push(header.key);
    }
  }

  return ordered;
}

function getInitialTicketColumnOrder() {
  const allColumns = ticketTableHeaders.map(header => header.key);

  if (typeof window === "undefined") {
    return allColumns;
  }

  try {
    const storedValue = window.localStorage.getItem(ticketColumnOrderStorageKey);

    if (!storedValue) {
      return allColumns;
    }

    const parsedValue = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) {
      return allColumns;
    }

    const validColumns = parsedValue.filter((value): value is TicketSortColumn =>
      typeof value === "string" && ticketTableHeaders.some(header => header.key === value),
    );

    return sanitizeTicketColumnOrder(validColumns);
  } catch {
    return allColumns;
  }
}

function getInitialTicketPageSize() {
  const defaultPageSize = 25;

  if (typeof window === "undefined") {
    return defaultPageSize;
  }

  try {
    const storedValue = window.localStorage.getItem(ticketPageSizeStorageKey);

    if (!storedValue) {
      return defaultPageSize;
    }

    const parsedValue = Number.parseInt(storedValue, 10);

    return ticketTablePageSizeOptions.includes(parsedValue as (typeof ticketTablePageSizeOptions)[number])
      ? parsedValue
      : defaultPageSize;
  } catch {
    return defaultPageSize;
  }
}

const ticketAssignmentFilterOptions: Array<{ value: TicketAssignmentFilter; label: string; detail: string }> = [
  { value: "any", label: "Any owner", detail: "Include all assignment states." },
  { value: "mine", label: "Assigned to me", detail: "Focus on your queue." },
  { value: "assigned", label: "Assigned", detail: "Only tickets with an owner." },
  { value: "unassigned", label: "Unassigned", detail: "Find tickets that need triage." },
];

const ticketScheduleFilterOptions: Array<{ value: TicketScheduleFilter; label: string; detail: string }> = [
  { value: "any", label: "Any schedule", detail: "Show scheduled and unscheduled work." },
  { value: "scheduled", label: "Scheduled", detail: "Tickets with any follow-up time set." },
  { value: "upcoming", label: "Upcoming", detail: "Scheduled for now or later." },
  { value: "overdue", label: "Overdue", detail: "Scheduled time has already passed." },
  { value: "unscheduled", label: "Unscheduled", detail: "No follow-up time has been set." },
];

const ticketUpdatedFilterOptions: Array<{ value: TicketUpdatedFilter; label: string; detail: string }> = [
  { value: "any", label: "Any time", detail: "No last-updated restriction." },
  { value: "24h", label: "Last 24 hours", detail: "Recently active tickets." },
  { value: "7d", label: "Last 7 days", detail: "Current weekly queue." },
  { value: "30d", label: "Last 30 days", detail: "Broad monthly review." },
];

function createEmptyTicketFilters(): TicketViewFilters {
  return {
    priorities: [],
    statuses: [],
    categories: [],
    customerIds: [],
    products: [],
    assignees: [],
    assignment: "any",
    schedule: "any",
    updatedWithin: "any",
  };
}

function toggleArrayFilterValue<T extends string>(values: T[], value: T) {
  return values.includes(value) ? values.filter(item => item !== value) : [...values, value];
}

function isTicketFilterSetEmpty(filters: TicketViewFilters) {
  return (
    filters.priorities.length === 0
    && filters.statuses.length === 0
    && filters.categories.length === 0
    && filters.customerIds.length === 0
    && filters.products.length === 0
    && filters.assignees.length === 0
    && filters.assignment === "any"
    && filters.schedule === "any"
    && filters.updatedWithin === "any"
  );
}

function getTicketFilterCount(filters: TicketViewFilters) {
  return (
    filters.priorities.length
    + filters.statuses.length
    + filters.categories.length
    + filters.customerIds.length
    + filters.products.length
    + filters.assignees.length
    + (filters.assignment === "any" ? 0 : 1)
    + (filters.schedule === "any" ? 0 : 1)
    + (filters.updatedWithin === "any" ? 0 : 1)
  );
}

function getUpdatedFilterCutoff(value: TicketUpdatedFilter, now: number) {
  switch (value) {
    case "24h":
      return now - 86_400_000;
    case "7d":
      return now - 7 * 86_400_000;
    case "30d":
      return now - 30 * 86_400_000;
    default:
      return Number.NEGATIVE_INFINITY;
  }
}

function matchesTicketViewFilters(ticket: Ticket, filters: TicketViewFilters, currentUser: string) {
  const now = Date.now();
  const scheduledAt = parseTicketScheduleAt(ticket.scheduleAt);
  const updatedAt = parseTimestampValue(ticket.lastUpdate, now);

  const priorityMatch = filters.priorities.length === 0 || filters.priorities.includes(ticket.priority);
  const statusMatch = filters.statuses.length === 0 || filters.statuses.includes(ticket.status);
  const categoryMatch = filters.categories.length === 0 || filters.categories.includes(ticket.category);
  const customerMatch = filters.customerIds.length === 0 || filters.customerIds.includes(ticket.customerId);
  const productMatch = filters.products.length === 0 || filters.products.includes(ticket.product);
  const assigneeMatch = filters.assignees.length === 0 || (ticket.assignee !== null && filters.assignees.includes(ticket.assignee));

  const assignmentMatch =
    filters.assignment === "any"
      ? true
      : filters.assignment === "mine"
        ? ticket.assignee === currentUser
        : filters.assignment === "assigned"
          ? Boolean(ticket.assignee)
          : ticket.assignee === null;

  const scheduleMatch =
    filters.schedule === "any"
      ? true
      : filters.schedule === "scheduled"
        ? ticket.scheduleAt !== null
        : filters.schedule === "unscheduled"
          ? ticket.scheduleAt === null
          : filters.schedule === "overdue"
            ? ticket.scheduleAt !== null && scheduledAt < now
            : ticket.scheduleAt !== null && scheduledAt >= now;

  const updatedMatch =
    filters.updatedWithin === "any"
      ? true
      : updatedAt >= getUpdatedFilterCutoff(filters.updatedWithin, now);

  return priorityMatch && statusMatch && categoryMatch && customerMatch && productMatch && assigneeMatch && assignmentMatch && scheduleMatch && updatedMatch;
}

function getAppliedFilterBadges(
  filters: TicketViewFilters,
  customerById: Map<string, Customer>,
  assignmentOptions: Array<{ value: TicketAssignmentFilter; label: string; detail: string }>,
  scheduleOptions: Array<{ value: TicketScheduleFilter; label: string; detail: string }>,
  updatedOptions: Array<{ value: TicketUpdatedFilter; label: string; detail: string }>,
) {
  return [
    ...filters.priorities.map(value => ({ key: `priority-${value}`, label: value, group: "priorities" as const, value })),
    ...filters.statuses.map(value => ({ key: `status-${value}`, label: value, group: "statuses" as const, value })),
    ...filters.categories.map(value => ({ key: `category-${value}`, label: value, group: "categories" as const, value })),
    ...filters.products.map(value => ({ key: `product-${value}`, label: value, group: "products" as const, value })),
    ...filters.assignees.map(value => ({ key: `assignee-${value}`, label: value, group: "assignees" as const, value })),
    ...filters.customerIds.map(value => ({
      key: `customer-${value}`,
      label: customerById.get(value)?.company ?? value,
      group: "customerIds" as const,
      value,
    })),
    ...(filters.assignment === "any"
      ? []
      : [{
          key: `assignment-${filters.assignment}`,
          label: assignmentOptions.find(option => option.value === filters.assignment)?.label ?? filters.assignment,
          group: "assignment" as const,
          value: filters.assignment,
        }]),
    ...(filters.schedule === "any"
      ? []
      : [{
          key: `schedule-${filters.schedule}`,
          label: scheduleOptions.find(option => option.value === filters.schedule)?.label ?? filters.schedule,
          group: "schedule" as const,
          value: filters.schedule,
        }]),
    ...(filters.updatedWithin === "any"
      ? []
      : [{
          key: `updated-${filters.updatedWithin}`,
          label: updatedOptions.find(option => option.value === filters.updatedWithin)?.label ?? filters.updatedWithin,
          group: "updatedWithin" as const,
          value: filters.updatedWithin,
        }]),
  ];
}

function getAppliedFilterBadgeClasses(group: keyof TicketViewFilters) {
  switch (group) {
    case "customerIds":
      return "ticket-filter-chip ticket-filter-chip--customer border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8] hover:bg-[#dbeafe]";
    case "products":
      return "ticket-filter-chip ticket-filter-chip--product border-[#c7d2fe] bg-[#eef2ff] text-[#4338ca] hover:bg-[#e0e7ff]";
    case "categories":
      return "ticket-filter-chip ticket-filter-chip--category border-[#bbf7d0] bg-[#ecfdf3] text-[#15803d] hover:bg-[#dcfce7]";
    case "assignees":
      return "ticket-filter-chip ticket-filter-chip--assignee border-[#f5d0fe] bg-[#fdf4ff] text-[#a21caf] hover:bg-[#fae8ff]";
    case "priorities":
      return "ticket-filter-chip ticket-filter-chip--priority border-[#fecaca] bg-[#fef2f2] text-[#b91c1c] hover:bg-[#fee2e2]";
    case "statuses":
      return "ticket-filter-chip ticket-filter-chip--status border-[#fde68a] bg-[#fffbeb] text-[#b45309] hover:bg-[#fef3c7]";
    case "assignment":
      return "ticket-filter-chip ticket-filter-chip--owner border-[#d1fae5] bg-[#ecfdf5] text-[#047857] hover:bg-[#d1fae5]";
    case "schedule":
      return "ticket-filter-chip ticket-filter-chip--schedule border-[#fed7aa] bg-[#fff7ed] text-[#c2410c] hover:bg-[#ffedd5]";
    case "updatedWithin":
      return "ticket-filter-chip ticket-filter-chip--updated border-[#d9d6fe] bg-[#f5f3ff] text-[#6d28d9] hover:bg-[#ede9fe]";
    default:
      return "ticket-filter-chip border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)] text-[#1a1c1d] hover:bg-[var(--brand-primary-soft-strong)]";
  }
}

function getAppliedFilterBadgeTypeLabel(group: keyof TicketViewFilters, copy: TicketsViewCopy) {
  switch (group) {
    case "customerIds":
      return copy.filterBadgeTypeLabels.customerIds;
    case "products":
      return copy.filterBadgeTypeLabels.products;
    case "categories":
      return copy.filterBadgeTypeLabels.categories;
    case "assignees":
      return copy.filterBadgeTypeLabels.assignees;
    case "priorities":
      return copy.filterBadgeTypeLabels.priorities;
    case "statuses":
      return copy.filterBadgeTypeLabels.statuses;
    case "assignment":
      return copy.filterBadgeTypeLabels.assignment;
    case "schedule":
      return copy.filterBadgeTypeLabels.schedule;
    case "updatedWithin":
      return copy.filterBadgeTypeLabels.updatedWithin;
    default:
      return copy.filterBadgeTypeLabels.default;
  }
}

function getFilterMultiSelectSummary(selectedValues: string[], options: FilterMultiSelectOption[], placeholder: string) {
  if (selectedValues.length === 0) {
    return placeholder;
  }

  const selectedLabels = selectedValues
    .map(value => options.find(option => option.value === value)?.label ?? value)
    .filter(Boolean);

  if (selectedLabels.length <= 2) {
    return selectedLabels.join(", ");
  }

  return `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;
}

function compareText(left: string | null | undefined, right: string | null | undefined) {
  return (left ?? "").localeCompare(right ?? "", undefined, { numeric: true, sensitivity: "base" });
}

function parseTicketScheduleAt(value: string | null) {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }

  const parsedDate = Date.parse(value);

  return Number.isNaN(parsedDate) ? Number.POSITIVE_INFINITY : parsedDate;
}

function getTicketPaginationItems(currentPage: number, totalPages: number) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: Array<number | "ellipsis"> = [1];
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  if (startPage > 2) {
    items.push("ellipsis");
  }

  for (let page = startPage; page <= endPage; page += 1) {
    items.push(page);
  }

  if (endPage < totalPages - 1) {
    items.push("ellipsis");
  }

  items.push(totalPages);

  return items;
}

function parseTicketCreatedAt(value: string | null | undefined) {
  const parsedValue = parseTimestampValue(value);
  return Number.isNaN(parsedValue) ? Number.NEGATIVE_INFINITY : parsedValue;
}

function getFirstAgentResponseDurationMs(ticket: Ticket) {
  const createdAt = parseTimestampValue(ticket.createdAt);

  if (Number.isNaN(createdAt)) {
    return null;
  }

  const notes = parseTicketNotes(ticket.notes);
  let firstResponseAt = Number.POSITIVE_INFINITY;

  for (const note of notes) {
    if (note.kind !== "external" || note.deliveryStatus === "received") {
      continue;
    }

    const noteTimestamp = parseTimestampValue(note.createdAt);

    if (Number.isNaN(noteTimestamp) || noteTimestamp < createdAt) {
      continue;
    }

    firstResponseAt = Math.min(firstResponseAt, noteTimestamp);
  }

  if (Number.isFinite(firstResponseAt)) {
    return firstResponseAt - createdAt;
  }

  if (notes.length > 0) {
    return null;
  }

  const lastUpdate = parseTimestampValue(ticket.lastUpdate);

  if (Number.isNaN(lastUpdate) || lastUpdate <= createdAt) {
    return null;
  }

  return lastUpdate - createdAt;
}

function formatAverageResponseDuration(ms: number, language: AppLanguage) {
  if (!Number.isFinite(ms) || ms <= 0) {
    return language === "ja" ? "1分未満" : "< 1 min";
  }

  const totalMinutes = Math.round(ms / 60_000);

  if (totalMinutes < 60) {
    return language === "ja"
      ? `${Math.max(1, totalMinutes)}分`
      : `${Math.max(1, totalMinutes)} min`;
  }

  const totalHours = totalMinutes / 60;

  if (totalHours < 24) {
    const roundedHours = Math.round(totalHours * 10) / 10;
    return language === "ja"
      ? `${Number.isInteger(roundedHours) ? roundedHours.toFixed(0) : roundedHours.toFixed(1)}時間`
      : `${Number.isInteger(roundedHours) ? roundedHours.toFixed(0) : roundedHours.toFixed(1)}h`;
  }

  const totalDays = totalHours / 24;
  const roundedDays = Math.round(totalDays * 10) / 10;

  return language === "ja"
    ? `${Number.isInteger(roundedDays) ? roundedDays.toFixed(0) : roundedDays.toFixed(1)}日`
    : `${Number.isInteger(roundedDays) ? roundedDays.toFixed(0) : roundedDays.toFixed(1)}d`;
}

function SortIndicatorIcon({ active, direction }: { active: boolean; direction?: TicketSortDirection }) {
  if (!active) {
    return (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.25 4.25 6 1.5l2.75 2.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.25 7.75 6 10.5l2.75-2.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return direction === "asc" ? (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.25 4.5 6 1.75 8.75 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 1.75v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.25 7.5 6 10.25 8.75 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 1.75v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function getSortIndicator(sortColumn: TicketSortColumn, sortDirection: TicketSortDirection, column: TicketSortColumn) {
  const isActive = sortColumn === column;

  return <SortIndicatorIcon active={isActive} direction={isActive ? sortDirection : undefined} />;
}

function getTicketPlanLabel(customer: Customer | null | undefined) {
  if (!customer) {
    return "Support";
  }

  return customer.products.find(product => /support/i.test(product.name))?.name ?? customer.products[0]?.name ?? "Support";
}

function getDefaultProduct(customer: Customer | null | undefined, noneLabel: string, supportLabel: string) {
  if (!customer) {
    return noneLabel;
  }

  return customer.products[0]?.name ?? supportLabel;
}

function hasTicketItem(productName: string | null | undefined, noneLabel: string) {
  const normalizedProductName = productName?.trim() ?? "";
  const normalizedNoneLabel = noneLabel.trim().toLowerCase();

  return Boolean(normalizedProductName)
    && normalizedProductName.toLowerCase() !== normalizedNoneLabel
    && normalizedProductName.toLowerCase() !== "none";
}

function getTicketItemDisplayLabel(productName: string | null | undefined, emptyLabel: string, noneLabel: string) {
  return hasTicketItem(productName, noneLabel) ? productName!.trim() : emptyLabel;
}

function getTicketItemOptions(currentProduct: string, customer: Customer | null | undefined, productPlans: ProductPlan[], noneLabel: string) {
  return [...new Set([
    ...(hasTicketItem(currentProduct, noneLabel) ? [currentProduct.trim()] : []),
    ...(customer?.products.map(product => product.name).filter(Boolean) ?? []),
    ...productPlans.map(plan => plan.name).filter(Boolean),
  ])].sort(compareText);
}

function getCreateTicketProductOptions(customer: Customer | null | undefined, noneLabel: string, supportLabel: string) {
  if (!customer) {
    return [noneLabel];
  }

  const items = customer.products.map(product => product.name).filter(Boolean);
  return items.length > 0 ? items : [supportLabel];
}

function getProductPlanForTicket(customer: Customer | null | undefined, productName: string, productPlans: ProductPlan[]) {
  if (!customer) {
    return null;
  }

  const normalizedProductName = productName.trim().toLowerCase();
  const matchedProduct = customer.products.find(product => product.name.trim().toLowerCase() === normalizedProductName) ?? null;

  if (matchedProduct?.productPlanId) {
    return productPlans.find(plan => plan.id === matchedProduct.productPlanId) ?? null;
  }

  return productPlans.find(plan => plan.name.trim().toLowerCase() === normalizedProductName) ?? null;
}

function getCreateTicketDraft(customers: Customer[], ticketCategories: TicketCategory[], currentUser: string, copy: TicketsViewCopy): TicketCreateDraft {
  const resolvedCustomer = customers[0] ?? null;
  const resolvedCategoryRecord = ticketCategories[0] ?? null;
  const resolvedCategory = resolvedCategoryRecord?.name ?? "General";

  return {
    subject: "",
    description: "",
    category: resolvedCategory,
    product: getDefaultProduct(resolvedCustomer, copy.none, copy.support),
    customerId: resolvedCustomer?.id ?? "",
    priority: "Medium",
    status: "Open",
    scheduleStartAt: "",
    scheduleAt: "",
    assignee: currentUser,
    customFieldValues: syncTicketCustomFieldValues(resolvedCategoryRecord?.customFields ?? [], {}),
  };
}

function AssigneePicker({ ticket, assigneeOptions, teamMembers, currentUserName, copy, onAssign }: AssigneePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; origin: "top" | "bottom" } | null>(null);

  const matchingAgents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return assigneeOptions.filter(agent => {
      if (ticket.assignee !== currentUserName && agent === currentUserName) {
        return false;
      }

      return normalizedSearch.length === 0 ? true : agent.toLowerCase().includes(normalizedSearch);
    });
  }, [assigneeOptions, currentUserName, search, ticket.assignee]);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const viewportPadding = 12;
    const estimatedMenuWidth = 256;
    const menuHeight = menuRef.current?.offsetHeight ?? 320;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;
    const unclampedLeft = rect.left;
    const left = Math.min(
      Math.max(viewportPadding, unclampedLeft),
      window.innerWidth - estimatedMenuWidth - viewportPadding,
    );

    setMenuPosition({
      top: shouldOpenUpward ? Math.max(viewportPadding, rect.top - menuHeight - 8) : rect.bottom + 8,
      left,
      origin: shouldOpenUpward ? "bottom" : "top",
    });
  }, []);

  // Measure the rendered menu and position it before paint so the very first
  // open uses the menu's real height instead of the fallback estimate.
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node) || menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
      setSearch("");
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const handleAssign = (assignee: string | null) => {
    onAssign(assignee);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative" onClick={event => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(current => !current)}
        className="flex w-[9.5rem] max-w-[9.5rem] items-start rounded-xl border-2 border-transparent bg-transparent px-3 py-2 text-left transition-colors hover:border-slate-300 focus-visible:border-slate-300 focus-visible:outline-none"
      >
        <AssigneeIdentity assignee={ticket.assignee} teamMembers={teamMembers} emptyLabel={copy.unassigned} emptyDetail={copy.needsOwner} assignedLabel={copy.assigned} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
      </button>

      {isOpen
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[200] w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.16)]"
              style={{
                top: menuPosition?.top ?? 0,
                left: menuPosition?.left ?? 0,
                visibility: menuPosition ? "visible" : "hidden",
              }}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
            >
          <div className="px-1 pb-2">
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              onClick={event => event.stopPropagation()}
              placeholder={copy.filterDialog.searchAssignees}
              autoFocus
              className="w-full rounded-xl border border-slate-200 bg-[#f9fafb] px-3 py-2 text-sm text-[#1a1c1d] outline-none transition focus:border-[#d0d5dd] focus:bg-white"
            />
          </div>

          <div className="space-y-1">
            {ticket.assignee !== null ? (
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  handleAssign(null);
                }}
                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold text-[#1a1c1d] transition-colors hover:bg-[#f3f4f6]"
              >
                <AssigneeIdentity assignee={null} teamMembers={teamMembers} emptyLabel={copy.unassigned} emptyDetail={copy.needsOwner} assignedLabel={copy.assigned} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
              </button>
            ) : null}

            {ticket.assignee !== currentUserName ? (
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  handleAssign(currentUserName);
                }}
                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold text-[#1a1c1d] transition-colors hover:bg-[#f3f4f6]"
              >
                <AssigneeIdentity assignee={currentUserName} teamMembers={teamMembers} emptyLabel={copy.unassigned} emptyDetail={copy.needsOwner} assignedLabel={copy.assigned} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
                <span className="brand-primary-text text-[10px] font-bold uppercase tracking-[0.2em]">{currentUserName === ticket.assignee ? copy.current : copy.you}</span>
              </button>
            ) : null}

            <div className="max-h-56 overflow-y-auto">
              {matchingAgents.map(agent => {
                const isAssigned = agent === ticket.assignee;

                return (
                  <button
                    key={agent}
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      handleAssign(agent);
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-[#1a1c1d] transition-colors hover:bg-[#f3f4f6]"
                  >
                    <AssigneeIdentity assignee={agent} teamMembers={teamMembers} emptyLabel={copy.unassigned} emptyDetail={copy.needsOwner} assignedLabel={copy.assigned} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
                    {isAssigned ? <span className="brand-primary-text text-xs font-semibold">{copy.assigned}</span> : null}
                  </button>
                );
              })}

              {matchingAgents.length === 0 ? <p className="px-3 py-2 text-sm text-slate-400">{copy.noMatchesFound}</p> : null}
            </div>
          </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function FilterMultiSelect({
  label,
  searchPlaceholder,
  emptyMessage,
  placeholder,
  copy,
  options,
  selectedValues,
  onChange,
}: {
  label: string;
  searchPlaceholder: string;
  emptyMessage: string;
  placeholder: string;
  copy: TicketsViewCopy;
  options: FilterMultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  const matchingOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter(option =>
      [option.label, option.detail, ...(option.keywords ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [options, search]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
      setSearch("");
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const summary = getFilterMultiSelectSummary(selectedValues, options, placeholder);

  return (
    <div ref={containerRef} className="ticket-filter-select relative">
      <button
        type="button"
        onClick={() => setIsOpen(current => !current)}
        className={`ticket-filter-select-trigger w-full rounded-[22px] border px-4 py-3 text-left transition-all ${isOpen ? "border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)] shadow-[0_12px_24px_rgba(var(--brand-primary-rgb),0.08)]" : "border-[#eaecf0] bg-[#fcfcfd] hover:bg-[#f8fafc]"}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{label}</p>
            <p className="mt-2 truncate text-sm font-semibold text-[#101828]">{summary}</p>
            <p className="mt-1 text-xs text-[#667085]">{selectedValues.length === 0 ? copy.noFiltersSelected : copy.selectedCount(selectedValues.length)}</p>
          </div>
          <span className="pt-1 text-sm text-[#667085]">▾</span>
        </div>
      </button>

      {isOpen ? (
        <div className="ticket-filter-select-menu absolute left-0 right-0 top-[calc(100%+0.75rem)] z-20 rounded-[24px] border border-[#eaecf0] bg-white p-3 shadow-[0_24px_48px_rgba(15,23,42,0.14)]">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
              className="ticket-filter-select-input w-full rounded-xl border border-slate-200 bg-[#f9fafb] px-3 py-2 text-sm text-[#1a1c1d] outline-none transition focus:border-[var(--brand-primary)] focus:bg-white focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
            />
            {selectedValues.length > 0 ? (
              <button
                type="button"
                onClick={() => onChange([])}
                className="ticket-filter-select-clear shrink-0 rounded-xl bg-[#f3f4f6] px-3 py-2 text-xs font-semibold text-[#475467] transition-colors hover:bg-[#e4e7ec]"
              >
                {copy.clearAll}
              </button>
            ) : null}
          </div>

          <div className="mt-3 max-h-64 space-y-1 overflow-y-auto pr-1">
            {matchingOptions.length > 0 ? (
              matchingOptions.map(option => {
                const active = selectedValues.includes(option.value);

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange(toggleArrayFilterValue(selectedValues, option.value))}
                    className={`ticket-filter-select-option flex w-full items-start justify-between gap-3 rounded-[18px] px-3 py-3 text-left transition-colors ${active ? "bg-[var(--brand-primary-soft)]" : "hover:bg-[#f8fafc]"}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#101828]">{option.label}</p>
                      {option.detail ? <p className="mt-1 text-xs text-[#667085]">{option.detail}</p> : null}
                    </div>
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] ${active ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white" : "border-[#d0d5dd] text-transparent"}`}>
                      ✓
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-sm text-slate-400">{emptyMessage}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TicketColumnVisibilityMenu({
  headers,
  visibleColumns,
  copy,
  onToggleColumn,
  onResetColumns,
  onReorderColumn,
}: {
  headers: typeof ticketTableHeaders;
  visibleColumns: TicketSortColumn[];
  copy: TicketsViewCopy;
  onToggleColumn: (column: TicketSortColumn) => void;
  onResetColumns: () => void;
  onReorderColumn: (sourceColumn: TicketSortColumn, targetColumn: TicketSortColumn) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<TicketSortColumn | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<TicketSortColumn | null>(null);
  const visibleColumnCount = visibleColumns.length;
  const hiddenColumnCount = headers.length - visibleColumnCount;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(current => !current)}
        className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${isOpen ? "bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] shadow-[0_12px_24px_rgba(var(--brand-primary-rgb),0.12)]" : "bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] hover:bg-[#f8fafc]"}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span>▥</span>
        <span>{copy.columnsButton}</span>
        <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-bold text-current">{visibleColumnCount}/{headers.length}</span>
      </button>

      <AnimatedModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        backdropAriaLabel={copy.columnDialog.close}
        dialogLabelledBy="ticket-columns-dialog-title"
        overlayClassName="fixed inset-0 z-[92] flex items-center justify-center bg-[#101828]/36 p-4 backdrop-blur-[4px] sm:p-6"
        dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] sm:p-7"
      >
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute top-6 right-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d] sm:top-7 sm:right-7"
            >
              ×
            </button>

            <div className="pr-14">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.columnDialog.eyebrow}</p>
                <h2 id="ticket-columns-dialog-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {copy.columnDialog.title}
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-[#667085]">
                  {copy.columnDialog.description}
                </p>
              </div>
            </div>

            <div className="mt-6 min-h-0 flex-1 rounded-[28px] border border-white/70 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.columnDialog.listTitle}</p>
                  <p className="mt-1 text-sm text-[#667085]">{copy.columnDialog.listDescription}</p>
                </div>
                {hiddenColumnCount > 0 ? (
                  <button
                    type="button"
                    onClick={onResetColumns}
                    className="rounded-xl bg-[#f3f4f6] px-3 py-2 text-xs font-semibold text-[#475467] transition-colors hover:bg-[#e4e7ec]"
                  >
                    {copy.columnDialog.resetAll}
                  </button>
                ) : null}
              </div>

              <div className="mt-4 h-[min(52vh,32rem)] overflow-y-auto pr-2">
                <div className="grid gap-3 sm:grid-cols-2">
                  {headers.map(header => {
                    const checked = visibleColumns.includes(header.key);
                    const locked = header.canHide === false;
                    const isDragging = draggedColumn === header.key;
                    const isDragOver = dragOverColumn === header.key && draggedColumn !== header.key;

                    return (
                      <label
                        key={header.key}
                        draggable
                        onDragStart={event => {
                          setDraggedColumn(header.key);
                          event.dataTransfer.effectAllowed = "move";
                        }}
                        onDragEnter={() => {
                          if (draggedColumn && draggedColumn !== header.key) {
                            setDragOverColumn(header.key);
                          }
                        }}
                        onDragOver={event => {
                          if (draggedColumn) {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                          }
                        }}
                        onDrop={event => {
                          event.preventDefault();
                          if (draggedColumn && draggedColumn !== header.key) {
                            onReorderColumn(draggedColumn, header.key);
                          }
                          setDraggedColumn(null);
                          setDragOverColumn(null);
                        }}
                        onDragEnd={() => {
                          setDraggedColumn(null);
                          setDragOverColumn(null);
                        }}
                        className={`flex items-center justify-between gap-3 rounded-[20px] border px-4 py-4 transition-colors ${isDragOver ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary-ring)]" : checked ? "border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)]" : "border-[#eaecf0] bg-[#fcfcfd] hover:bg-[#f8fafc]"} ${isDragging ? "opacity-50" : ""}`}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span
                            aria-label={copy.columnDialog.reorderHandleLabel}
                            title={copy.columnDialog.reorderHandleLabel}
                            className="flex h-6 w-5 shrink-0 cursor-grab items-center justify-center text-base leading-none text-slate-400 active:cursor-grabbing"
                          >
                            ⠿
                          </span>
                          <span className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#101828]">{header.label}</p>
                            <p className="mt-1 text-xs text-[#667085]">{locked ? copy.columnDialog.alwaysShown : checked ? copy.columnDialog.visibleInTable : copy.columnDialog.hiddenFromTable}</p>
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          {locked ? <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{copy.columnDialog.locked}</span> : null}
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            onChange={() => onToggleColumn(header.key)}
                            className="h-4 w-4 rounded border-slate-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
                          />
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#eaecf0] pt-6">
              <p className="text-sm text-[#667085]">{copy.columnDialog.changesApplyImmediately}</p>
              <div className="flex flex-wrap items-center gap-3">
                {hiddenColumnCount > 0 ? (
                  <button
                    type="button"
                    onClick={onResetColumns}
                    className="rounded-xl bg-white px-4 py-3 text-sm font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                  >
                    {copy.columnDialog.showAllColumns}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="brand-primary-button rounded-xl px-5 py-3 text-sm font-bold transition-all"
                >
                  {copy.columnDialog.apply}
                </button>
              </div>
            </div>
      </AnimatedModal>
    </>
  );
}

export function TicketsView({
  language,
  customers,
  productPlans,
  ticketCategories,
  teamMembers,
  currentUser,
  timezone,
  canEditTickets = true,
  isLoadingTickets = false,
  activeTab,
  onTabChange,
  filteredTickets,
  syncingTicketIds = [],
  selectedTicket,
  selectedCustomer,
  isQuickViewOpen,
  counts,
  onSelectTicket,
  onCreateTicket,
  onAssignTicket,
  onUpdateCategory,
  onUpdateProduct,
  onUpdatePriority,
  onUpdateStatus,
  onOpenTicket,
  onOpenQuickView,
  onCloseQuickView,
}: TicketsViewProps) {
  const copy = ticketsViewCopy[language];
  const hoverTimerRef = useRef<number | null>(null);
  const syncingTicketIdSet = useMemo(() => new Set(syncingTicketIds), [syncingTicketIds]);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [sortColumn, setSortColumn] = useState<TicketSortColumn>("lastUpdate");
  const [sortDirection, setSortDirection] = useState<TicketSortDirection>("desc");
  const [pageSize, setPageSize] = useState(() => getInitialTicketPageSize());
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [createTicketError, setCreateTicketError] = useState("");
  const [visibleColumns, setVisibleColumns] = useState<TicketSortColumn[]>(() => getInitialVisibleTicketColumns());
  const [columnOrder, setColumnOrder] = useState<TicketSortColumn[]>(() => getInitialTicketColumnOrder());
  const [appliedFilters, setAppliedFilters] = useState<TicketViewFilters>(() => createEmptyTicketFilters());
  const [draftFilters, setDraftFilters] = useState<TicketViewFilters>(() => createEmptyTicketFilters());
  const ticketCategoryNames = useMemo(
    () => ticketCategories.map(category => category.name),
    [ticketCategories],
  );
  const [createTicketDraft, setCreateTicketDraft] = useState<TicketCreateDraft>(() =>
    getCreateTicketDraft(customers, ticketCategories, currentUser, copy),
  );
  const previewScheduleStartAt = normalizeDateTimeForStorage(createTicketDraft.scheduleStartAt, timezone);
  const previewScheduleAt = normalizeDateTimeForStorage(createTicketDraft.scheduleAt, timezone);
  const selectedCreateTicketCategory = useMemo(
    () => getTicketCategoryByName(ticketCategories, createTicketDraft.category),
    [createTicketDraft.category, ticketCategories],
  );
  const createTicketCustomFields = selectedCreateTicketCategory?.customFields ?? [];

  const customerById = useMemo(() => new Map(customers.map(customer => [customer.id, customer])), [customers]);
  const createTicketCustomer = customerById.get(createTicketDraft.customerId) ?? null;
  const availableItems = useMemo(
    () => getCreateTicketProductOptions(createTicketCustomer, copy.none, copy.support),
    [copy.none, copy.support, createTicketCustomer],
  );
  const selectedCreateTicketPlan = useMemo(
    () => getProductPlanForTicket(createTicketCustomer, createTicketDraft.product, productPlans),
    [createTicketCustomer, createTicketDraft.product, productPlans],
  );
  const createTicketSla = selectedCreateTicketPlan?.sla?.trim() ?? "";
  const assigneeOptions = useMemo(
    () => [...new Set([
      ...getAssignableAssigneeNames(teamMembers, ticketCategories, null),
      ...filteredTickets.map(ticket => ticket.assignee).filter((value): value is string => Boolean(value)),
    ])].sort(compareText),
    [filteredTickets, teamMembers, ticketCategories],
  );
  const createTicketAssigneeOptions = useMemo(
    () => getAssignableAssigneeNames(teamMembers, ticketCategories, createTicketDraft.category),
    [createTicketDraft.category, teamMembers, ticketCategories],
  );
  const productOptions = useMemo(
    () => [...new Set([...customers.flatMap(customer => customer.products.map(product => product.name)), ...filteredTickets.map(ticket => ticket.product)])].filter(Boolean).sort(compareText),
    [customers, filteredTickets],
  );
  const customerOptions = useMemo(
    () => [...customers].sort((left, right) => compareText(left.company, right.company)),
    [customers],
  );
  const customerFilterOptions = useMemo<FilterMultiSelectOption[]>(
    () => customerOptions.map(customer => ({
      value: customer.id,
      label: customer.company,
      detail: `${customer.name} · ${customer.health}`,
      keywords: [customer.name, customer.email, customer.region],
    })),
    [customerOptions],
  );

  useEffect(() => {
    setCurrentTime(Date.now());

    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);
  const assigneeFilterOptions = useMemo<FilterMultiSelectOption[]>(
    () => assigneeOptions.map(assignee => ({
      value: assignee,
      label: assignee,
      detail: assignee === currentUser ? copy.current : copy.support,
    })),
    [assigneeOptions, copy.current, copy.support, currentUser],
  );
  const localizedHeaders = useMemo(
    () => ticketTableHeaders.map(header => ({ ...header, label: copy.tableHeaders[header.key] })),
    [copy],
  );
  const orderedLocalizedHeaders = useMemo(
    () => columnOrder
      .map(key => localizedHeaders.find(header => header.key === key))
      .filter((header): header is (typeof localizedHeaders)[number] => Boolean(header)),
    [columnOrder, localizedHeaders],
  );
  const localizedAssignmentFilterOptions = useMemo(
    () => ticketAssignmentFilterOptions.map(option => ({ ...option, label: copy.assignmentFilters[option.value].label, detail: copy.assignmentFilters[option.value].detail })),
    [copy],
  );
  const localizedScheduleFilterOptions = useMemo(
    () => ticketScheduleFilterOptions.map(option => ({ ...option, label: copy.scheduleFilters[option.value].label, detail: copy.scheduleFilters[option.value].detail })),
    [copy],
  );
  const localizedUpdatedFilterOptions = useMemo(
    () => ticketUpdatedFilterOptions.map(option => ({ ...option, label: copy.updatedFilters[option.value].label, detail: copy.updatedFilters[option.value].detail })),
    [copy],
  );
  const filterPreviewCount = useMemo(
    () => filteredTickets.filter(ticket => matchesTicketViewFilters(ticket, draftFilters, currentUser)).length,
    [currentUser, draftFilters, filteredTickets],
  );
  const visibleHeaders = useMemo(
    () => orderedLocalizedHeaders.filter(header => visibleColumns.includes(header.key)),
    [orderedLocalizedHeaders, visibleColumns],
  );
  const viewFilteredTickets = useMemo(
    () => filteredTickets.filter(ticket => matchesTicketViewFilters(ticket, appliedFilters, currentUser)),
    [appliedFilters, currentUser, filteredTickets],
  );
  const avgResponseSummary = useMemo(() => {
    const responseDurations = viewFilteredTickets
      .map(getFirstAgentResponseDurationMs)
      .filter((value): value is number => value !== null);

    const averageResponseMs = responseDurations.length > 0
      ? responseDurations.reduce((total, value) => total + value, 0) / responseDurations.length
      : null;

    return {
      averageResponseMs,
      respondedCount: responseDurations.length,
      totalCount: viewFilteredTickets.length,
    };
  }, [viewFilteredTickets]);
  const activeFilterCount = useMemo(() => getTicketFilterCount(appliedFilters), [appliedFilters]);
  const appliedFilterBadges = useMemo(
    () => getAppliedFilterBadges(appliedFilters, customerById, localizedAssignmentFilterOptions, localizedScheduleFilterOptions, localizedUpdatedFilterOptions),
    [appliedFilters, customerById, localizedAssignmentFilterOptions, localizedScheduleFilterOptions, localizedUpdatedFilterOptions],
  );
  const visibleSelectedTicket = viewFilteredTickets.find(ticket => ticket.id === selectedTicket.id) ?? viewFilteredTickets[0] ?? selectedTicket;
  const visibleSelectedCustomer = customerById.get(visibleSelectedTicket.customerId) ?? selectedCustomer;

  useEffect(() => {
    if (!isCreateTicketOpen) {
      return;
    }

    setCreateTicketDraft(current => {
      const resolvedCustomer = current.customerId ? customerById.get(current.customerId) ?? customers[0] ?? null : null;
      const resolvedCategory = ticketCategoryNames.includes(current.category) ? current.category : (ticketCategoryNames[0] ?? "General");
      const productOptions = getCreateTicketProductOptions(resolvedCustomer, copy.none, copy.support);

      return {
        ...current,
        customerId: resolvedCustomer?.id ?? "",
        category: resolvedCategory,
        product: productOptions.includes(current.product) ? current.product : getDefaultProduct(resolvedCustomer, copy.none, copy.support),
        customFieldValues: syncTicketCustomFieldValues(
          getTicketCategoryByName(ticketCategories, resolvedCategory)?.customFields ?? [],
          current.customFieldValues,
        ),
      };
    });
  }, [copy.none, copy.support, customerById, customers, isCreateTicketOpen, ticketCategories, ticketCategoryNames]);

  useEffect(() => {
    if (!isCreateTicketOpen) {
      return;
    }

    setCreateTicketDraft(current => {
      if (!current.assignee) {
        return current;
      }

      if (createTicketAssigneeOptions.includes(current.assignee)) {
        return current;
      }

      return {
        ...current,
        assignee: createTicketAssigneeOptions.includes(currentUser) ? currentUser : createTicketAssigneeOptions[0] ?? "",
      };
    });
  }, [createTicketAssigneeOptions, currentUser, isCreateTicketOpen]);

  useEffect(() => {
    if (!isCreateTicketOpen) {
      return;
    }

    setCreateTicketDraft(current => ({
      ...current,
      customFieldValues: syncTicketCustomFieldValues(createTicketCustomFields, current.customFieldValues),
    }));
  }, [createTicketCustomFields, isCreateTicketOpen]);

  useEffect(() => {
    if (!isFilterModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFilterModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterModalOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(ticketColumnStorageKey, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(ticketColumnOrderStorageKey, JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(ticketPageSizeStorageKey, String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    if (visibleColumns.includes(sortColumn)) {
      return;
    }

    const fallbackColumn = visibleColumns.includes("lastUpdate")
      ? "lastUpdate"
      : visibleColumns.includes("duration")
        ? "duration"
        : visibleColumns[0] ?? "subject";

    setSortColumn(fallbackColumn);
    setSortDirection(fallbackColumn === "lastUpdate" || fallbackColumn === "duration" ? "desc" : "asc");
  }, [sortColumn, visibleColumns]);

  useEffect(() => {
    if (viewFilteredTickets.length === 0) {
      if (isQuickViewOpen) {
        onCloseQuickView();
      }
      return;
    }

    if (!viewFilteredTickets.some(ticket => ticket.id === selectedTicket.id)) {
      onSelectTicket(viewFilteredTickets[0]!.id);
      if (isQuickViewOpen) {
        onCloseQuickView();
      }
    }
  }, [isQuickViewOpen, onCloseQuickView, onSelectTicket, selectedTicket.id, viewFilteredTickets]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, appliedFilters]);

  const sortedTickets = useMemo(() => {
    const directionMultiplier = sortDirection === "asc" ? 1 : -1;

    return [...viewFilteredTickets].sort((left, right) => {
      const leftCustomer = customerById.get(left.customerId)?.name ?? "";
      const rightCustomer = customerById.get(right.customerId)?.name ?? "";

      let comparison = 0;

      switch (sortColumn) {
        case "id":
          comparison = compareText(left.id, right.id);
          break;
        case "subject":
          comparison = compareText(left.subject, right.subject);
          break;
        case "category":
          comparison = compareText(left.category, right.category);
          break;
        case "customer":
          comparison = compareText(leftCustomer, rightCustomer);
          break;
        case "item":
          comparison = compareText(left.product, right.product);
          break;
        case "sla":
          comparison = (getTicketSlaDeadlineTimestamp(left.createdAt, left.sla, currentTime, left.slaPausedAt, left.slaPausedTotalMs) ?? Number.POSITIVE_INFINITY)
            - (getTicketSlaDeadlineTimestamp(right.createdAt, right.sla, currentTime, right.slaPausedAt, right.slaPausedTotalMs) ?? Number.POSITIVE_INFINITY);
          break;
        case "assignee":
          comparison = compareText(left.assignee ?? "Unassigned", right.assignee ?? "Unassigned");
          break;
        case "priority":
          comparison = ticketPriorityOrder[left.priority] - ticketPriorityOrder[right.priority];
          break;
        case "status":
          comparison = ticketStatusOrder[left.status] - ticketStatusOrder[right.status];
          break;
        case "duration":
          comparison = parseTicketCreatedAt(right.createdAt) - parseTicketCreatedAt(left.createdAt);
          break;
        case "schedule":
          comparison = parseTicketScheduleAt(left.scheduleAt) - parseTicketScheduleAt(right.scheduleAt);
          break;
        case "lastUpdate":
          comparison = parseTimestampValue(left.lastUpdate) - parseTimestampValue(right.lastUpdate);
          break;
      }

      if (comparison === 0) {
        comparison = compareText(left.id, right.id);
      }

      return comparison * directionMultiplier;
    });
  }, [currentTime, customerById, sortColumn, sortDirection, viewFilteredTickets]);
  const totalPages = Math.max(1, Math.ceil(sortedTickets.length / pageSize));
  const currentPageStartIndex = sortedTickets.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const currentPageEndIndex = Math.min(currentPageStartIndex + pageSize, sortedTickets.length);
  const paginatedTickets = useMemo(
    () => sortedTickets.slice(currentPageStartIndex, currentPageEndIndex),
    [currentPageEndIndex, currentPageStartIndex, sortedTickets],
  );
  const paginationItems = useMemo(
    () => getTicketPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    setCurrentPage(current => Math.min(current, totalPages));
  }, [totalPages]);

  const clearHoverTimer = () => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const handleTicketHoverStart = (id: string) => {
    clearHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      onOpenQuickView(id);
      hoverTimerRef.current = null;
    }, 1000);
  };

  useEffect(() => clearHoverTimer, []);

  const handleSort = (column: TicketSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(currentDirection => (currentDirection === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection(column === "lastUpdate" || column === "duration" ? "desc" : "asc");
  };

  const handleOpenCreateTicket = () => {
    setCreateTicketDraft(getCreateTicketDraft(customers, ticketCategories, currentUser, copy));
    setCreateTicketError("");
    setIsCreateTicketOpen(true);
  };

  const handleOpenFilterModal = () => {
    setDraftFilters(appliedFilters);
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setIsFilterModalOpen(false);
  };

  const handleClearFilters = () => {
    const emptyFilters = createEmptyTicketFilters();
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const handleToggleColumn = (column: TicketSortColumn) => {
    if (requiredTicketColumns.includes(column)) {
      return;
    }

    setVisibleColumns(current => sanitizeVisibleTicketColumns(
      current.includes(column) ? current.filter(item => item !== column) : [...current, column],
    ));
  };

  const handleResetColumns = () => {
    setVisibleColumns(ticketTableHeaders.map(header => header.key));
    setColumnOrder(ticketTableHeaders.map(header => header.key));
  };

  const handleReorderColumn = (sourceColumn: TicketSortColumn, targetColumn: TicketSortColumn) => {
    if (sourceColumn === targetColumn) {
      return;
    }

    setColumnOrder(current => {
      const next = sanitizeTicketColumnOrder(current);
      const fromIndex = next.indexOf(sourceColumn);
      const toIndex = next.indexOf(targetColumn);

      if (fromIndex === -1 || toIndex === -1) {
        return current;
      }

      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, sourceColumn);

      return next;
    });
  };

  const handleRemoveAppliedFilter = (group: keyof TicketViewFilters, value?: string) => {
    setAppliedFilters(current => {
      if (group === "assignment" || group === "schedule" || group === "updatedWithin") {
        return { ...current, [group]: "any" } as TicketViewFilters;
      }

      if (!value) {
        return current;
      }

      return {
        ...current,
        [group]: current[group].filter(item => item !== value),
      } as TicketViewFilters;
    });
  };

  const handleDraftPreset = (preset: "urgent" | "needs-owner" | "scheduled" | "recent") => {
    setDraftFilters(current => {
      switch (preset) {
        case "urgent":
          return {
            ...current,
            priorities: ["High"],
            statuses: current.statuses.length > 0 ? current.statuses.filter(status => status !== "Resolved") : ["Open", "In Progress", "Pending"],
          };
        case "needs-owner":
          return { ...current, assignment: "unassigned", assignees: [] };
        case "scheduled":
          return { ...current, schedule: "scheduled" };
        case "recent":
          return { ...current, updatedWithin: "24h" };
        default:
          return current;
      }
    });
  };

  const handleCreateTicket = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!createTicketDraft.subject.trim() || !createTicketDraft.category.trim() || !createTicketDraft.product.trim()) {
      return;
    }

    const customFieldError = getCustomFieldValidationError(createTicketCustomFields, createTicketDraft.customFieldValues, copy);

    if (customFieldError) {
      setCreateTicketError(customFieldError);
      return;
    }

    setCreateTicketError("");

    onCreateTicket({
      subject: createTicketDraft.subject.trim(),
      description: createTicketDraft.description.trim(),
      category: createTicketDraft.category.trim(),
      product: createTicketDraft.product.trim(),
      customerId: createTicketDraft.customerId,
      priority: createTicketDraft.priority,
      status: createTicketDraft.status,
      scheduleStartAt: normalizeDateTimeForStorage(createTicketDraft.scheduleStartAt, timezone),
      scheduleAt: normalizeDateTimeForStorage(createTicketDraft.scheduleAt, timezone),
      assignee: createTicketDraft.assignee.trim() || null,
      customFieldValues: syncTicketCustomFieldValues(createTicketCustomFields, createTicketDraft.customFieldValues),
    });

    setIsCreateTicketOpen(false);
    setCreateTicketError("");
    setCreateTicketDraft(getCreateTicketDraft(customers, ticketCategories, currentUser, copy));
  };

  return (
    <>
      <section className="space-y-8 p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.pageEyebrow}</p>
            <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.pageTitle}</h1>
            <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.pageDescription}</p>
          </div>

          <div className="flex items-center gap-3">
            {canEditTickets ? (
              <button
                type="button"
                onClick={handleOpenCreateTicket}
                className="brand-primary-button flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-all"
              >
                <span>＋</span>
                <span>{copy.newTicket}</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <TicketStatCard title={copy.stats.openTickets.title} value={String(counts.open)} detail={copy.stats.openTickets.detail} accent="text-[#1a1c1d]" isLoading={isLoadingTickets} />
          <TicketStatCard
            title={copy.stats.avgResponse.title}
            value={avgResponseSummary.averageResponseMs === null ? copy.stats.avgResponse.emptyValue : formatAverageResponseDuration(avgResponseSummary.averageResponseMs, language)}
            detail={avgResponseSummary.averageResponseMs === null
              ? copy.stats.avgResponse.emptyDetail
              : copy.stats.avgResponse.sampledDetail(avgResponseSummary.respondedCount, avgResponseSummary.totalCount)}
            accent="text-[#1a1c1d]"
            isLoading={isLoadingTickets}
          />
          <TicketStatCard title={copy.stats.unassigned.title} value={formatCount(counts.unassigned)} detail={copy.stats.unassigned.detail} accent="text-[#ba1a1a]" isLoading={isLoadingTickets} />
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-4 px-2 py-1">
            <nav className="flex flex-wrap gap-6">
              {ticketTabs.map(tab => {
                const count = tab === "All Tickets" ? counts.all : tab === "Assigned to Me" ? counts.assigned : counts.unassigned;
                const active = activeTab === tab;

                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => onTabChange(tab)}
                    className={`relative pb-2 text-sm transition-colors ${active ? "brand-primary-text font-semibold" : "brand-primary-hover-text font-medium text-slate-500"}`}
                  >
                    <span>{copy.tabLabels[tab]}</span>
                    <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${active ? "brand-primary-pill" : "bg-slate-100 text-slate-400"}`}>{count}</span>
                    {active ? <span className="brand-primary-solid absolute bottom-0 left-0 h-0.5 w-full" /> : null}
                  </button>
                );
              })}
            </nav>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-[#475467]">
                <span>{copy.displayLimit}</span>
                <select
                  value={pageSize}
                  onChange={event => {
                    const nextValue = Number.parseInt(event.target.value, 10);
                    setPageSize(ticketTablePageSizeOptions.includes(nextValue as (typeof ticketTablePageSizeOptions)[number]) ? nextValue : 25);
                    setCurrentPage(1);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                  aria-label={copy.displayLimit}
                >
                  {ticketTablePageSizeOptions.map(option => (
                    <option key={option} value={option}>{copy.displayLimitOption(option)}</option>
                  ))}
                </select>
              </label>

              <TicketColumnVisibilityMenu
                headers={orderedLocalizedHeaders}
                visibleColumns={visibleColumns}
                copy={copy}
                onToggleColumn={handleToggleColumn}
                onResetColumns={handleResetColumns}
                onReorderColumn={handleReorderColumn}
              />

              <button
                type="button"
                onClick={handleOpenFilterModal}
                className="brand-primary-pill brand-primary-hover-soft flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all"
              >
                <span>≡</span>
                <span>{copy.filterButton}</span>
                {activeFilterCount > 0 ? <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-bold text-[var(--brand-primary)]">{activeFilterCount}</span> : null}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 px-2">
            <span className="rounded-full bg-[#f8fafc] px-3 py-1 text-[11px] font-semibold text-[#475467]">
              {copy.resultsCount(sortedTickets.length)}
            </span>
            <span className="rounded-full bg-[#f8fafc] px-3 py-1 text-[11px] font-semibold text-[#475467]">
              {copy.columnsShown(visibleHeaders.length, ticketTableHeaders.length)}
            </span>
            {activeFilterCount > 0 ? (
              <>
                {appliedFilterBadges.map(filter => (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => handleRemoveAppliedFilter(filter.group, filter.value)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${getAppliedFilterBadgeClasses(filter.group)}`}
                  >
                    <span className="ticket-filter-chip-type rounded-full bg-white/70 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-current/80">
                      {getAppliedFilterBadgeTypeLabel(filter.group, copy)}
                    </span>
                    <span>{filter.label}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                >
                  {copy.clearAll}
                </button>
              </>
            ) : (
              <p className="text-xs text-slate-500">{copy.noFilterHint}</p>
            )}
          </div>

          <div className="ticket-table-shell relative overflow-hidden rounded-[32px] border border-white/60 bg-white shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            {isLoadingTickets ? (
              <div className={`pointer-events-none absolute inset-x-0 z-[1] flex ${sortedTickets.length === 0 ? "inset-y-0 items-center justify-center bg-white/75 backdrop-blur-[1px]" : "bottom-14 items-center justify-center px-5"}`}>
                <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
                    <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span>Loading tickets…</span>
                </div>
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="ticket-table w-full border-collapse text-left" style={{ minWidth: `${Math.max(visibleHeaders.length * 140, 760)}px` }}>
              <thead className="ticket-table-head bg-[#f3f3f4]/80">
                <tr>
                  {visibleHeaders.map(header => (
                    <th
                      key={header.key}
                      className={`py-5 text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400 ${header.className}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(header.key)}
                        className={`flex items-center gap-2 text-sm font-semibold normal-case tracking-normal transition-colors ${header.key === "priority" ? "mx-auto" : ""} hover:text-[#475467]`}
                        style={{ letterSpacing: "0em" }}
                        aria-label={copy.sortBy(header.label, sortColumn === header.key, sortDirection)}
                      >
                        <span>{header.label}</span>
                        <span className={`inline-flex items-center ${sortColumn === header.key ? "text-[#475467]" : "text-slate-300"}`} aria-hidden="true">
                          {getSortIndicator(sortColumn, sortDirection, header.key)}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="ticket-table-body">
                {paginatedTickets.map(ticket => {
                  const customer = customerById.get(ticket.customerId) ?? null;
                  const ticketCreatedAt = ticket.createdAt;
                  const slaCountdown = getTicketSlaCountdownInfo(ticket.createdAt, ticket.sla, currentTime, ticket.slaPausedAt, ticket.slaPausedTotalMs);
                  const isSyncingTicket = syncingTicketIdSet.has(ticket.id);

                  return (
                    <tr
                      key={ticket.id}
                      className="group cursor-pointer transition-colors hover:bg-[var(--brand-primary-soft)]"
                      onClick={() => {
                        onSelectTicket(ticket.id);
                        onOpenTicket(ticket.id);
                      }}
                    >
                      {visibleHeaders.map(header => {
                        switch (header.key) {
                          case "id":
                            return (
                        <td key="id" className="brand-primary-text w-[8.5rem] px-8 py-5 text-sm font-bold whitespace-nowrap">
                          <span
                            className="inline-flex items-center gap-2 rounded-lg px-1 py-0.5 transition-colors hover:bg-[var(--brand-primary-soft)]"
                            onMouseEnter={() => handleTicketHoverStart(ticket.id)}
                            onMouseLeave={clearHoverTimer}
                          >
                            {isSyncingTicket ? (
                              <span className="inline-flex h-4 w-4 items-center justify-center text-[var(--brand-primary)]" aria-label="Syncing" title="Syncing">
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
                                  <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                              </span>
                            ) : null}
                            #{ticket.id}
                          </span>
                        </td>
                            );
                          case "subject":
                            return (
                        <td key="subject" className="px-6 py-5">
                          <div className="text-sm font-bold leading-tight text-[#1a1c1d]">{ticket.subject}</div>
                        </td>
                            );
                          case "category":
                            return (
                        <td key="category" className="px-6 py-5">
                          {canEditTickets ? (
                            <TicketPillMenu
                              value={ticket.category}
                              options={ticketCategoryNames}
                              ariaLabel={`${copy.quickView.changeCategory} ${ticket.id}`}
                              onChange={category => onUpdateCategory(ticket.id, category)}
                              buttonClassName="inline-flex items-center gap-2 rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]"
                              renderValue={category => <span>{category}</span>}
                              renderOption={category => <span>{category}</span>}
                            />
                          ) : <span className="inline-flex items-center rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{ticket.category}</span>}
                        </td>
                            );
                          case "customer":
                            return (
                        <td key="customer" className="px-6 py-5">
                          <div className="text-sm font-semibold text-[#1a1c1d]">{customer?.company || copy.none}</div>
                        </td>
                            );
                          case "item":
                            return (
                        <td key="item" className="px-6 py-5">
                          <div className="text-sm font-semibold text-[#1a1c1d]">{getTicketItemDisplayLabel(ticket.product, copy.none, copy.none)}</div>
                        </td>
                            );
                          case "sla":
                            return (
                        <td key="sla" className="w-[12rem] px-6 py-5 whitespace-nowrap text-sm font-medium text-slate-500">
                          {ticket.sla ? (
                            <div className="space-y-1">
                              <div className="font-semibold text-[#1a1c1d]">{formatTicketSlaLabel(ticket.sla)}</div>
                              {slaCountdown ? (
                                <div className={`text-xs font-semibold ${slaCountdown.isPaused ? "text-[#c2410c]" : slaCountdown.isOverdue ? "text-[#b42318]" : "text-[#175cd3]"}`}>
                                  {slaCountdown.isPaused ? `${copy.quickView.paused} · ${slaCountdown.displayLabel} ${copy.quickView.left}` : `${slaCountdown.displayLabel} ${slaCountdown.isOverdue ? copy.quickView.over : copy.quickView.left}`}
                                </div>
                              ) : null}
                            </div>
                          ) : <span className="text-slate-400">{copy.quickView.noSla}</span>}
                        </td>
                            );
                          case "priority":
                            return (
                        <td key="priority" className="px-6 py-5 text-center">
                          {canEditTickets ? (
                            <TicketPillMenu
                              value={ticket.priority}
                              options={ticketPriorities}
                              ariaLabel={`${copy.quickView.changePriority} ${ticket.id}`}
                              onChange={priority => onUpdatePriority(ticket.id, priority)}
                              buttonClassName={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[ticket.priority]}`}
                              renderValue={priority => (
                                <>
                                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                                  <span>{copy.priorityLabels[priority]}</span>
                                </>
                              )}
                              renderOption={priority => (
                                <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[priority]}`}>
                                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                                  <span>{copy.priorityLabels[priority]}</span>
                                </span>
                              )}
                            />
                          ) : <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[ticket.priority]}`}>{copy.priorityLabels[ticket.priority]}</span>}
                        </td>
                            );
                          case "status":
                            return (
                        <td key="status" className="w-[10rem] px-6 py-5 whitespace-nowrap">
                          {canEditTickets ? (
                            <TicketPillMenu
                              value={ticket.status}
                              options={ticketStatuses}
                              ariaLabel={`${copy.quickView.changeStatus} ${ticket.id}`}
                              onChange={status => onUpdateStatus(ticket.id, status)}
                              buttonClassName={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#f8fafc] px-2.5 py-1 text-xs font-semibold ${statusClasses[ticket.status]}`}
                              renderValue={status => (
                                <>
                                  <span className="text-sm">{getStatusIcon(status)}</span>
                                  <span>{copy.statusLabels[status]}</span>
                                </>
                              )}
                              renderOption={status => (
                                <>
                                  <span className="text-base leading-none">{getStatusIcon(status)}</span>
                                  <span>{copy.statusLabels[status]}</span>
                                </>
                              )}
                              getOptionClassName={status => statusClasses[status]}
                            />
                          ) : <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#f8fafc] px-2.5 py-1 text-xs font-semibold ${statusClasses[ticket.status]}`}>{copy.statusLabels[ticket.status]}</span>}
                        </td>
                            );
                          case "duration":
                            return (
                        <td key="duration" className="w-[10rem] px-6 py-5 whitespace-nowrap text-sm font-medium text-slate-500">
                          <span className="font-semibold text-[#1a1c1d]"><TicketDurationText value={ticketCreatedAt} /></span>
                        </td>
                            );
                          case "schedule":
                            return (
                        <td key="schedule" className="w-[12rem] px-6 py-5 whitespace-nowrap text-sm font-medium text-slate-500">
                          {ticket.scheduleAt || ticket.scheduleStartAt ? formatScheduleRangeLabel(ticket.scheduleStartAt, ticket.scheduleAt, copy.createDialog.noScheduleSet, timezone) : <span className="text-slate-400">{copy.createDialog.noScheduleSet}</span>}
                        </td>
                            );
                          case "assignee":
                            return (
                        <td key="assignee" className="w-[11rem] px-4 py-5">
                          {canEditTickets ? <AssigneePicker ticket={ticket} assigneeOptions={getAssignableAssigneeNames(teamMembers, ticketCategories, ticket.category)} teamMembers={teamMembers} currentUserName={currentUser} copy={copy} onAssign={assignee => onAssignTicket(ticket.id, assignee)} /> : <div className="px-3 py-2 text-left"><AssigneeIdentity assignee={ticket.assignee} teamMembers={teamMembers} emptyLabel={copy.unassigned} emptyDetail={copy.needsOwner} assignedLabel={copy.assigned} /></div>}
                        </td>
                            );
                          case "lastUpdate":
                            return (
                        <td key="lastUpdate" className="w-[14rem] px-6 py-5 whitespace-nowrap text-xs font-medium text-slate-500">
                          <TicketLastUpdateText value={ticket.lastUpdate} timezone={timezone} className="text-xs font-medium text-slate-500" />
                        </td>
                            );
                          default:
                            return null;
                        }
                      })}
                    </tr>
                  );
                })}
                {sortedTickets.length === 0 && !isLoadingTickets ? (
                  <tr>
                    <td colSpan={visibleHeaders.length} className="px-8 py-16 text-center">
                      <p className="font-headline text-2xl font-bold tracking-[-0.04em] text-[#1a1c1d]">{copy.noMatchingTicketsTitle}</p>
                      <p className="mt-2 text-sm text-slate-500">
                        {activeFilterCount > 0
                          ? copy.noMatchingTicketsFiltered
                          : copy.noMatchingTicketsDefault}
                      </p>
                      {activeFilterCount > 0 ? (
                        <button
                          type="button"
                          onClick={handleClearFilters}
                          className="mt-4 rounded-xl bg-[#f3f4f6] px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#e4e7ec]"
                        >
                          {copy.clearFilters}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              </table>
            </div>

            <div className="grid items-center gap-4 bg-[#f3f3f4]/40 px-8 py-5 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-400">
                  {copy.showingSummary(paginatedTickets.length, sortedTickets.length, activeFilterCount)}
                </p>
              </div>
              <div className="justify-self-start md:justify-self-center">
                <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
                  {copy.pageSummary(currentPage, totalPages)}
                </span>
              </div>
              <div className="flex items-center gap-2 md:justify-self-end">
                <button
                  type="button"
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  aria-label={copy.previousPage}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition-all enabled:hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ‹
                </button>
                {paginationItems.map((item, index) => item === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} className="flex h-8 min-w-8 items-center justify-center px-1 text-xs font-bold text-slate-400">…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    aria-label={copy.pageNumber(item)}
                    aria-current={currentPage === item ? "page" : undefined}
                    className={currentPage === item
                      ? "brand-primary-solid flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-bold"
                      : "flex h-8 min-w-8 items-center justify-center rounded-lg border border-slate-200 px-2 text-xs font-bold text-slate-600 transition-all hover:bg-white"}
                  >
                    {item}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages || sortedTickets.length === 0}
                  aria-label={copy.nextPage}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition-all enabled:hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {isQuickViewOpen ? (
          <TicketQuickView
            ticket={visibleSelectedTicket}
            customer={visibleSelectedCustomer}
            productPlans={productPlans}
            timezone={timezone}
            copy={copy}
            currentTime={currentTime}
            currentUser={currentUser}
            ticketCategories={ticketCategories}
            teamMembers={teamMembers}
            canEditTickets={canEditTickets}
            onAssignTicket={onAssignTicket}
            onUpdateCategory={onUpdateCategory}
            onUpdateProduct={onUpdateProduct}
            onUpdatePriority={onUpdatePriority}
            onUpdateStatus={onUpdateStatus}
            onOpenTicket={onOpenTicket}
            onClose={onCloseQuickView}
          />
        ) : null}
      </section>

      <AnimatedModal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        backdropAriaLabel={copy.filterDialog.close}
        dialogLabelledBy="ticket-filter-dialog-title"
        overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog ticket-filter-modal relative z-10 max-h-[calc(100vh-4rem)] w-full max-w-6xl overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.filterDialog.eyebrow}</p>
                <h2 id="ticket-filter-dialog-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {copy.filterDialog.title}
                </h2>
                <p className="mt-2 max-w-3xl text-sm text-[#667085]">
                  {copy.filterDialog.description}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
              >
                ×
              </button>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.55fr)]">
              <div className="ticket-filter-panel rounded-[28px] border border-white/70 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Quick presets</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {[
                    { key: "urgent", label: copy.filterDialog.quickPresets.urgent.label, detail: copy.filterDialog.quickPresets.urgent.detail },
                    { key: "needs-owner", label: copy.filterDialog.quickPresets["needs-owner"].label, detail: copy.filterDialog.quickPresets["needs-owner"].detail },
                    { key: "scheduled", label: copy.filterDialog.quickPresets.scheduled.label, detail: copy.filterDialog.quickPresets.scheduled.detail },
                    { key: "recent", label: copy.filterDialog.quickPresets.recent.label, detail: copy.filterDialog.quickPresets.recent.detail },
                  ].map(preset => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => handleDraftPreset(preset.key as "urgent" | "needs-owner" | "scheduled" | "recent")}
                      className="ticket-filter-preset rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3 text-left transition-all hover:border-[var(--brand-primary-ring)] hover:bg-[var(--brand-primary-soft)]"
                    >
                      <p className="text-sm font-semibold text-[#101828]">{preset.label}</p>
                      <p className="mt-1 text-xs text-[#667085]">{preset.detail}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="ticket-filter-preview rounded-[28px] border border-white/70 bg-[linear-gradient(180deg,#ffffff,#f8fafc)] p-5 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.filterDialog.preview}</p>
                <p className="mt-3 text-4xl font-extrabold tracking-[-0.05em] text-[#101828]">{filterPreviewCount}</p>
                <p className="mt-2 text-sm text-[#667085]">{copy.filterDialog.previewDescription}</p>
                <div className="ticket-filter-preview-surface mt-4 rounded-[20px] bg-white/90 p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{copy.filterDialog.baseScope}</p>
                  <p className="mt-2 text-sm font-semibold text-[#101828]">{copy.filterDialog.baseScopeSummary(copy.tabLabels[activeTab], filteredTickets.length)}</p>
                  <p className="mt-1 text-xs text-[#667085]">{copy.filterDialog.baseScopeDescription}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <section className="ticket-filter-panel rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.filterDialog.statusAndPriority}</p>
                    <p className="mt-1 text-sm text-[#667085]">{copy.filterDialog.statusAndPriorityDescription}</p>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{copy.filterDialog.priority}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ticketPriorities.map(priority => {
                      const active = draftFilters.priorities.includes(priority);

                      return (
                        <button
                          key={priority}
                          type="button"
                          onClick={() => setDraftFilters(current => ({ ...current, priorities: toggleArrayFilterValue(current.priorities, priority) }))}
                          className={`rounded-full px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] transition-all ${active ? priorityClasses[priority] : "bg-[#f3f4f6] text-[#475467] hover:bg-[#e4e7ec]"}`}
                        >
                          {copy.priorityLabels[priority]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{copy.filterDialog.status}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ticketStatuses.map(status => {
                      const active = draftFilters.statuses.includes(status);

                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setDraftFilters(current => ({ ...current, statuses: toggleArrayFilterValue(current.statuses, status) }))}
                          className={`ticket-filter-option-chip ticket-filter-option-chip--status inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition-all ${active ? `ticket-filter-option-chip--active ${statusClasses[status]} bg-[var(--brand-primary-soft)] shadow-[0_10px_22px_rgba(var(--brand-primary-rgb),0.08)]` : "bg-[#f8fafc] text-[#475467] hover:bg-[#eef2f6]"}`}
                        >
                          <span>{getStatusIcon(status)}</span>
                          <span>{copy.statusLabels[status]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="ticket-filter-panel rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.filterDialog.assignment}</p>
                  <p className="mt-1 text-sm text-[#667085]">{copy.filterDialog.assignmentDescription}</p>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {localizedAssignmentFilterOptions.map(option => {
                    const active = draftFilters.assignment === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setDraftFilters(current => ({ ...current, assignment: option.value }))}
                        className={`ticket-filter-choice ticket-filter-choice--assignment rounded-[22px] border px-4 py-4 text-left transition-all ${active ? "ticket-filter-choice--active border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)] shadow-[0_12px_24px_rgba(var(--brand-primary-rgb),0.08)]" : "border-[#eaecf0] bg-[#fcfcfd] hover:bg-[#f8fafc]"}`}
                      >
                        <p className="text-sm font-semibold text-[#101828]">{option.label}</p>
                        <p className="mt-1 text-xs text-[#667085]">{option.detail}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5">
                  <FilterMultiSelect
                    label={copy.filterDialog.assignees}
                    searchPlaceholder={copy.filterDialog.searchAssignees}
                    emptyMessage={copy.filterDialog.noAssigneeSearchMatches}
                    placeholder={copy.filterDialog.allAssignees}
                    copy={copy}
                    options={assigneeFilterOptions}
                    selectedValues={draftFilters.assignees}
                    onChange={values => setDraftFilters(current => ({ ...current, assignees: values }))}
                  />
                </div>
              </section>

              <section className="ticket-filter-panel rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.filterDialog.categoryAndProduct}</p>
                  <p className="mt-1 text-sm text-[#667085]">{copy.filterDialog.categoryAndProductDescription}</p>
                </div>

                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{copy.filterDialog.categories}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ticketCategoryNames.map(category => {
                      const active = draftFilters.categories.includes(category);

                      return (
                        <button
                          key={category}
                          type="button"
                          onClick={() => setDraftFilters(current => ({ ...current, categories: toggleArrayFilterValue(current.categories, category) }))}
                          className={`ticket-filter-option-chip ticket-filter-option-chip--category rounded-full px-3 py-2 text-xs font-semibold transition-all ${active ? "ticket-filter-option-chip--active bg-[var(--brand-primary-soft)] text-[#101828] shadow-[0_10px_20px_rgba(var(--brand-primary-rgb),0.08)]" : "bg-[#f8fafc] text-[#475467] hover:bg-[#eef2f6]"}`}
                        >
                          {category}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{copy.filterDialog.products}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {productOptions.map(product => {
                      const active = draftFilters.products.includes(product);

                      return (
                        <button
                          key={product}
                          type="button"
                          onClick={() => setDraftFilters(current => ({ ...current, products: toggleArrayFilterValue(current.products, product) }))}
                          className={`ticket-filter-option-chip ticket-filter-option-chip--product rounded-full px-3 py-2 text-xs font-semibold transition-all ${active ? "ticket-filter-option-chip--active bg-[var(--brand-primary-soft)] text-[#101828] shadow-[0_10px_20px_rgba(var(--brand-primary-rgb),0.08)]" : "bg-[#f8fafc] text-[#475467] hover:bg-[#eef2f6]"}`}
                        >
                          {product}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="ticket-filter-panel rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.filterDialog.customerScheduleRecency}</p>
                  <p className="mt-1 text-sm text-[#667085]">{copy.filterDialog.customerScheduleRecencyDescription}</p>
                </div>

                <div className="mt-5">
                  <FilterMultiSelect
                    label={copy.filterDialog.customers}
                    searchPlaceholder={copy.filterDialog.searchCustomers}
                    emptyMessage={copy.filterDialog.noCustomerSearchMatches}
                    placeholder={copy.filterDialog.allCustomers}
                    copy={copy}
                    options={customerFilterOptions}
                    selectedValues={draftFilters.customerIds}
                    onChange={values => setDraftFilters(current => ({ ...current, customerIds: values }))}
                  />
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{copy.filterDialog.schedule}</p>
                    <div className="mt-3 space-y-2">
                      {localizedScheduleFilterOptions.map(option => {
                        const active = draftFilters.schedule === option.value;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setDraftFilters(current => ({ ...current, schedule: option.value }))}
                            className={`ticket-filter-choice ticket-filter-choice--schedule w-full rounded-[18px] border px-3 py-3 text-left transition-all ${active ? "ticket-filter-choice--active border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)]" : "border-[#eaecf0] bg-[#fcfcfd] hover:bg-[#f8fafc]"}`}
                          >
                            <p className="text-sm font-semibold text-[#101828]">{option.label}</p>
                            <p className="mt-1 text-xs text-[#667085]">{option.detail}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{copy.filterDialog.lastUpdated}</p>
                    <div className="mt-3 space-y-2">
                      {localizedUpdatedFilterOptions.map(option => {
                        const active = draftFilters.updatedWithin === option.value;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setDraftFilters(current => ({ ...current, updatedWithin: option.value }))}
                            className={`ticket-filter-choice ticket-filter-choice--updated w-full rounded-[18px] border px-3 py-3 text-left transition-all ${active ? "ticket-filter-choice--active border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)]" : "border-[#eaecf0] bg-[#fcfcfd] hover:bg-[#f8fafc]"}`}
                          >
                            <p className="text-sm font-semibold text-[#101828]">{option.label}</p>
                            <p className="mt-1 text-xs text-[#667085]">{option.detail}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#eaecf0] pt-6">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDraftFilters(createEmptyTicketFilters())}
                  className="rounded-xl bg-[#f3f4f6] px-4 py-3 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#e4e7ec]"
                >
                  {copy.filterDialog.resetDraft}
                </button>
                {!isTicketFilterSetEmpty(appliedFilters) ? (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="rounded-xl bg-white px-4 py-3 text-sm font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                  >
                    {copy.filterDialog.clearActiveFilters}
                  </button>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsFilterModalOpen(false)}
                  className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
                >
                  {copy.filterDialog.cancel}
                </button>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all"
                >
                  {copy.filterDialog.applyFilters(filterPreviewCount)}
                </button>
              </div>
            </div>
      </AnimatedModal>

      <AnimatedModal
        open={isCreateTicketOpen && canEditTickets}
        onClose={() => setIsCreateTicketOpen(false)}
        backdropAriaLabel={copy.createDialog.close}
        overlayClassName="fixed inset-0 z-[80] bg-[#101828]/36 p-4 backdrop-blur-[4px] sm:p-6"
        dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)] sm:max-h-[calc(100vh-3rem)]"
      >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.createDialog.eyebrow}</p>
                  <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.createDialog.title}</h2>
                  <p className="mt-2 text-sm text-[#667085]">{copy.createDialog.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateTicketOpen(false);
                    setCreateTicketError("");
                    setCreateTicketDraft(getCreateTicketDraft(customers, ticketCategories, currentUser, copy));
                  }}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
                >
                  ×
                </button>
              </div>

              <div className="mt-6 min-h-0 flex-1 overflow-y-auto pr-1">
                <form onSubmit={handleCreateTicket} className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(260px,0.85fr)]">
                  <div className="space-y-5">
                    <label className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.subject}</span>
                      <input
                        type="text"
                        value={createTicketDraft.subject}
                        onChange={event => setCreateTicketDraft(current => ({ ...current, subject: event.target.value }))}
                        placeholder={copy.createDialog.subjectPlaceholder}
                        required
                        className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                      />
                    </label>

                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.customer}</span>
                    <select
                      value={createTicketDraft.customerId}
                      onChange={event => {
                        const nextCustomerId = event.target.value;
                        const nextCustomer = customerById.get(nextCustomerId) ?? null;

                        setCreateTicketDraft(current => ({
                          ...current,
                          customerId: nextCustomerId,
                          product: getDefaultProduct(nextCustomer, copy.none, copy.support),
                        }));
                      }}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    >
                      <option value="">{copy.none}</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.company}
                        </option>
                      ))}
                    </select>
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.category}</span>
                    <select
                      value={createTicketDraft.category}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, category: event.target.value }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    >
                      {ticketCategoryNames.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.product}</span>
                    <select
                      value={createTicketDraft.product}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, product: event.target.value }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    >
                      {availableItems.map(product => (
                        <option key={product} value={product}>
                          {product}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-[#667085]">
                      {!createTicketCustomer
                        ? copy.createDialog.noCustomerSelectedProductHelp
                        : availableItems[0] === copy.support && availableItems.length === 1 && createTicketCustomer.products.length === 0
                          ? copy.createDialog.noAssignedProductsHelp
                          : copy.createDialog.assignedItemsHelp(availableItems.length)}
                    </p>
                    {createTicketSla ? <p className="mt-2 text-xs font-semibold text-[#027a48]">{copy.createDialog.ticketSla(createTicketSla)}</p> : null}
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.assignee}</span>
                    <select
                      value={createTicketDraft.assignee}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, assignee: event.target.value }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    >
                      <option value="">{copy.unassigned}</option>
                      {createTicketAssigneeOptions.map(agent => (
                        <option key={agent} value={agent}>
                          {agent}
                        </option>
                      ))}
                    </select>
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.priority}</span>
                    <select
                      value={createTicketDraft.priority}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, priority: event.target.value as TicketPriority }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    >
                      {ticketPriorities.map(priority => (
                        <option key={priority} value={priority}>
                          {copy.priorityLabels[priority]}
                        </option>
                      ))}
                    </select>
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.status}</span>
                    <select
                      value={createTicketDraft.status}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, status: event.target.value as TicketStatus }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    >
                      {ticketStatuses.map(status => (
                        <option key={status} value={status}>
                          {copy.statusLabels[status]}
                        </option>
                      ))}
                    </select>
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.scheduleStart}</span>
                    <input
                      type="datetime-local"
                      value={createTicketDraft.scheduleStartAt}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, scheduleStartAt: event.target.value }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    />
                    <p className="mt-2 text-xs text-[#667085]">{copy.createDialog.scheduleStartHelp(timezone)}</p>
                      </label>

                      <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.schedule}</span>
                    <input
                      type="datetime-local"
                      value={createTicketDraft.scheduleAt}
                      onChange={event => setCreateTicketDraft(current => ({ ...current, scheduleAt: event.target.value }))}
                      className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                    />
                    <p className="mt-2 text-xs text-[#667085]">{copy.createDialog.scheduleHelp(timezone)}</p>
                      </label>
                    </div>

                    <label className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.descriptionLabel}</span>
                      <textarea
                        value={createTicketDraft.description}
                        onChange={event => setCreateTicketDraft(current => ({ ...current, description: event.target.value }))}
                        placeholder={copy.createDialog.descriptionPlaceholder}
                        rows={6}
                        className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                      />
                    </label>

                    {createTicketCustomFields.length > 0 ? (
                      <div className="rounded-[28px] border border-[#eaecf0] bg-white p-5 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.createDialog.categoryFields}</p>
                            <p className="mt-2 text-sm text-[#667085]">{copy.createDialog.categoryFieldsDescription(createTicketDraft.category)}</p>
                          </div>
                          <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">
                            {copy.createDialog.fieldsCount(createTicketCustomFields.length)}
                          </span>
                        </div>

                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          {createTicketCustomFields.map(field => (
                            <label key={field.id} className={`block ${field.type === "textarea" ? "md:col-span-2" : ""}`}>
                              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">
                                {field.label}
                                {field.required ? " *" : ""}
                              </span>
                              {field.type === "textarea" ? (
                                <textarea
                                  value={createTicketDraft.customFieldValues[field.id] ?? ""}
                                  onChange={event => {
                                    const nextValue = event.target.value;
                                    setCreateTicketDraft(current => ({
                                      ...current,
                                      customFieldValues: { ...current.customFieldValues, [field.id]: nextValue },
                                    }));
                                    setCreateTicketError("");
                                  }}
                                  rows={4}
                                  className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                                />
                              ) : field.type === "select" ? (
                                <select
                                  value={createTicketDraft.customFieldValues[field.id] ?? ""}
                                  onChange={event => {
                                    const nextValue = event.target.value;
                                    setCreateTicketDraft(current => ({
                                      ...current,
                                      customFieldValues: { ...current.customFieldValues, [field.id]: nextValue },
                                    }));
                                    setCreateTicketError("");
                                  }}
                                  className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                                >
                                  <option value="">{copy.createDialog.selectField(field.label)}</option>
                                  {field.options.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type={field.type === "date" ? "date" : "text"}
                                  value={createTicketDraft.customFieldValues[field.id] ?? ""}
                                  onChange={event => {
                                    const nextValue = event.target.value;
                                    setCreateTicketDraft(current => ({
                                      ...current,
                                      customFieldValues: { ...current.customFieldValues, [field.id]: nextValue },
                                    }));
                                    setCreateTicketError("");
                                  }}
                                  className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                                />
                              )}
                              {field.helpText ? <p className="mt-2 text-xs text-[#667085]">{field.helpText}</p> : null}
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {createTicketError ? <p className="text-sm font-semibold text-[#b42318]">{createTicketError}</p> : null}
                  </div>

                  <div className="rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.createDialog.ticketPreview}</p>
                    <div className="mt-4 rounded-[24px] bg-[#f9f9fa] p-5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.createDialog.customer}</p>
                      <p className="mt-2 text-lg font-bold tracking-[-0.03em] text-[#101828]">{createTicketCustomer?.company ?? copy.none}</p>
                      <p className="mt-1 text-sm text-[#667085]">{createTicketCustomer?.name ?? copy.createDialog.noCustomerLinked}</p>
                      {createTicketDraft.description ? <p className="mt-4 whitespace-pre-line text-sm leading-6 text-[#475467]">{createTicketDraft.description}</p> : null}

                      <div className="mt-5 grid gap-3 text-sm text-[#475467]">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[#98a2b3]">{copy.createDialog.product}</span>
                          <span className="font-semibold text-[#101828]">{createTicketDraft.product || copy.none}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[#98a2b3]">SLA</span>
                          <span className="text-right font-semibold text-[#101828]">{createTicketSla || copy.createDialog.noSla}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[#98a2b3]">{copy.createDialog.priority}</span>
                          <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[createTicketDraft.priority]}`}>
                            {copy.priorityLabels[createTicketDraft.priority]}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[#98a2b3]">{copy.createDialog.status}</span>
                          <span className={`inline-flex items-center gap-1.5 rounded-full bg-[#f8fafc] px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${statusClasses[createTicketDraft.status]}`}>
                            <span>{getStatusIcon(createTicketDraft.status)}</span>
                            <span>{copy.statusLabels[createTicketDraft.status]}</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[#98a2b3]">{copy.createDialog.schedule}</span>
                          <span className="text-right font-semibold text-[#101828]">{formatScheduleRangeLabel(previewScheduleStartAt, previewScheduleAt, copy.createDialog.noScheduleSet, timezone)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-[#98a2b3]">{copy.createDialog.assignee}</span>
                          <span className="font-semibold text-[#101828]">{createTicketDraft.assignee || copy.unassigned}</span>
                        </div>
                        {createTicketCustomFields.map(field => (
                          <div key={field.id} className="flex items-center justify-between gap-4">
                            <span className="text-[#98a2b3]">{field.label}</span>
                            <span className="text-right font-semibold text-[#101828]">{createTicketDraft.customFieldValues[field.id] || copy.createDialog.fieldEmpty}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-5 rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] p-5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.createDialog.nextSteps}</p>
                      <ul className="mt-3 space-y-2 text-sm leading-6 text-[#667085]">
                        <li>{copy.createDialog.nextStepOne}</li>
                        <li>{copy.createDialog.nextStepTwo}</li>
                      </ul>
                    </div>

                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreateTicketOpen(false);
                          setCreateTicketError("");
                        }}
                        className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
                      >
                        {copy.createDialog.cancel}
                      </button>
                      <button type="submit" className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all">
                        {copy.createDialog.createTicket}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
      </AnimatedModal>
    </>
  );
}
